import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  Mail,
  MessageSquare,
  Send,
  Beaker,
  Users,
  Clock,
  Eye,
  Code,
  Check,
  Lock,
  Upload,
  UserCircle,
  Phone,
  LayoutTemplate,
  Save,
  Blocks,
  Search,
  X,
  AlertCircle,
  Maximize2,
  SlidersHorizontal,
  Database,
  Link2,
  Paperclip,
  FileText,
  Trash2,
  Loader2,
  Copy,
  UserPlus,
} from "lucide-react";
import {
  fetchCampaign,
  createCampaign,
  updateCampaign,
  testSendCampaign,
  fetchSegments,
  previewSegment,
  createSegment,
  updateSegment,
  fetchBuyerMatchTable,
  fetchBuyerMatchCriteria,
  searchBuyersToAdd,
  refreshBuyersForChannel,
  fetchTemplates,
  createTemplate,
  fetchMergeTags,
  checkMergeTags,
  fetchPropertyEmailData,
  fetchSmsCreditBalance,
  uploadCampaignAttachment,
  deleteCampaignAttachment,
  duplicateCampaign,
  fetchSegment,
  searchContactsForList,
  quickAddContact,
} from "../api/communicationsApi";
import { getAgents } from "../../../api/agent";
import EmailBuilder from "../components/EmailBuilder";
import BuyerMatchSearchPanel from "../components/BuyerMatchSearchPanel";
import AiWritePanel from "../components/AiWritePanel";
import MessageExtras from "../components/MessageExtras";
import MessageBlocksTail from "../components/MessageBlocksTail";
import SmsIntroHead from "../components/SmsIntroHead";
import SmsSendFrom from "../components/SmsSendFrom";
import PropertyPickerPanel from "../components/PropertyPickerPanel";
import TemplatePickerModal from "../components/TemplatePickerModal";
import {
  substituteTags,
  unresolvedTags,
  unresolvedSummary,
} from "../utils/mergeTags";
import {
  appendEmailBlocks,
  assembleSms,
  splitEmailBlocks,
  splitSmsBlocks,
  pricedText,
  OFF_MARKET_TAG,
  PREFERENCES_TAG,
  PREFERENCES_SENTENCE_SMS,
  buildSmsAgentBlock,
  stripSmsAgentBlock,
} from "../utils/messageBlocks";
import {
  buildPropertyEmailHtml,
  buildPropertyJustListedSms,
  propertyEmailMeta,
  propertyEmailIntro,
  propertyListEmailMeta,
  propertyListEmailIntro,
  removePropertyPatch,
} from "../utils/propertyEmail";
import {
  smsInfo, asSent, smsLength, SMS_MAX_CHARS, SMS_STOP_PLACEHOLDER, appendStopPlaceholder, withSmsIntro,
} from "../utils/smsCredits";
import useSmsSender, { COLLINGS_SENDER } from "../utils/useSmsSender";
import { derivePreviewFromBody } from "../utils/previewText";
import { formatMelbourneDateTime, datetimeLocalToUtcISOString } from "../../../utils/melbourneTime";
import PreSendDialog from "../../presend/components/PreSendDialog";
// Same Mapbox helpers the "Find Buyers for a Property" panel uses: the backend
// has no geocoder, so surrounding-suburb distances are computed here.
import { geocodePlace, enrichSuburbsWithDistance } from "../../analytics/utils/geocode";

const CONTACT_TYPES = ["buyer", "seller", "tenant", "landlord", "investor", "buyer_advocate", "general"];
const INTENT = ["cold", "warm", "active", "hot", "priority"];

// A campaign's own list of people added one by one. Kept out of the "Your
// lists" picker - it belongs to one campaign and shows as "Add people" instead.
const HAND_LIST_PREFIX = "Added by hand · ";
const isHandList = (s) => (s?.name || "").startsWith(HAND_LIST_PREFIX);

// Best guess at which box a search that found nobody was meant for, so
// "Add as a new person" opens with it already filled in.
const guessNewPerson = (q) => {
  const v = (q || "").trim();
  if (v.includes("@")) return { name: "", email: v, phone: "" };
  if (/^[\d\s()+-]{6,}$/.test(v)) return { name: "", email: "", phone: v };
  return { name: v, email: "", phone: "" };
};

const AU_STATE_OPTIONS = [
  { abbr: "VIC", name: "Victoria" },
  { abbr: "NSW", name: "New South Wales" },
  { abbr: "QLD", name: "Queensland" },
  { abbr: "SA", name: "South Australia" },
  { abbr: "WA", name: "Western Australia" },
  { abbr: "TAS", name: "Tasmania" },
  { abbr: "NT", name: "Northern Territory" },
  { abbr: "ACT", name: "Australian Capital Territory" },
];

/** In-app (not browser-native) state autocomplete, styled to match the CRM. */
function StateAutocomplete({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const q = (value || "").toLowerCase().trim();
  const matches = AU_STATE_OPTIONS.filter(
    (o) => !q || o.abbr.toLowerCase().includes(q) || o.name.toLowerCase().includes(q)
  );
  return (
    <div className="relative">
      <input
        value={value}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        placeholder="State (VIC)"
        className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
      />
      {open && matches.length > 0 && (
        <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-52 overflow-y-auto">
          {matches.map((o) => (
            <button
              key={o.abbr}
              type="button"
              onMouseDown={() => { onChange(o.abbr); setOpen(false); }}
              className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 flex items-center gap-2"
            >
              <span className="font-semibold text-gray-800 w-10">{o.abbr}</span>
              <span className="text-xs text-gray-400">{o.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** "VIC" and "Victoria" are the same state - match either spelling. */
function stateMatches(rowState, wanted) {
  const w = (wanted || "").trim().toLowerCase();
  if (!w) return true;
  const r = (rowState || "").trim().toLowerCase();
  if (!r) return false;
  const o = AU_STATE_OPTIONS.find((x) => x.abbr.toLowerCase() === w || x.name.toLowerCase() === w);
  return o ? [o.abbr.toLowerCase(), o.name.toLowerCase()].includes(r) : r.includes(w);
}

/** Short money for the budget column: 1250000 -> "$1.25m", 850000 -> "$850k". */
function shortMoney(value) {
  const n = Number(String(value ?? "").replace(/[^0-9.]/g, ""));
  if (!n) return "";
  if (n >= 1000000) return `$${(n / 1000000).toFixed(n % 1000000 === 0 ? 0 : 2)}m`;
  if (n >= 1000) return `$${Math.round(n / 1000)}k`;
  return `$${n}`;
}

function budgetLabel(row) {
  const min = shortMoney(row.pref_price_min);
  const max = shortMoney(row.pref_price_max);
  if (min && max) return min === max ? max : `${min} - ${max}`;
  return max || min || "Any";
}

function bedsLabel(row) {
  const min = String(row.pref_bedrooms_min ?? "").trim();
  const max = String(row.pref_bedrooms_max ?? "").trim();
  if (min && max) return min === max ? min : `${min}-${max}`;
  return max || min || "Any";
}

// A listing's Property.property_type mapped onto the Buyers Match "Type" option,
// so the search starts on the listing's own type (same map the Find Buyers panel
// uses). Anything unmapped starts as "Any".
const LISTING_TYPE_TO_OPTION = {
  house: "House", apartment: "Apartment", unit: "Unit", townhouse: "Townhouse",
  villa: "Villa", land: "Land", blocks_of_units: "Block of Units",
  house_and_land: "House and Land",
};

const numOrEmpty = (v) => (v === null || v === undefined || v === "" ? "" : String(v));
const numOrNull = (v) => {
  const s = String(v ?? "").trim();
  if (!s) return null;
  const n = Number(s);
  return Number.isNaN(n) ? null : n;
};

// Turn a DRF error response into one human-readable line so a failed save shows
// the real reason (e.g. "subject: This field is required.") instead of a bare
// "Save failed". The campaign endpoints answer with {detail: ...} for general
// errors and {field: [msg]} for validation, so handle both; fall back otherwise.
export const apiErrorMessage = (err, fallback) => {
  const data = err?.response?.data;
  if (!data) return fallback;
  if (typeof data === "string") {
    // A request the server turns away before it reaches the campaign code comes
    // back as a whole HTML error page, and the agent saw the raw markup in the
    // pop-up (CG, 26 Oct 2026). Show its title instead.
    if (/<html|<!doctype/i.test(data)) {
      const title = (data.match(/<title>([^<]*)<\/title>/i) || [])[1];
      return title ? `${fallback}: ${title.trim()}` : fallback;
    }
    return data;
  }
  if (data.detail) return data.detail;
  for (const [field, val] of Object.entries(data)) {
    const msg = Array.isArray(val) ? val[0] : val;
    if (typeof msg === "string" && msg.trim()) {
      return field === "non_field_errors" ? msg : `${field}: ${msg}`;
    }
  }
  return fallback;
};

/** Seed the Buyers Match requirements from what the listing implies. Bedrooms,
 *  bathrooms and car spaces stay blank on purpose: an exact bedroom count would
 *  silently gut a campaign audience (a buyer who never stated beds still matches,
 *  one who stated 2 would not). The agent can set them if they want to. */
const criteriaFromDefaults = (d = {}, radiusKm = 5) => ({
  suburb: d.suburb || "",
  state: d.state || "",
  price_min: numOrEmpty(d.price_min),
  price_max: numOrEmpty(d.price_max),
  beds_min: "", beds_max: "",
  baths_min: "", baths_max: "",
  cars_min: "", cars_max: "",
  property_type: LISTING_TYPE_TO_OPTION[(d.property_type || "").toLowerCase()] || "",
  radius_km: radiusKm || 5,
});

const MATCH_TONE = (score) =>
  score >= 80 ? "bg-green-100 text-green-700"
    : score >= 60 ? "bg-teal-100 text-teal-700"
      : score >= 40 ? "bg-amber-100 text-amber-700"
        : "bg-gray-100 text-gray-600";

/**
 * The Buyers Match picker: every buyer whose SAVED preferences match the chosen
 * listing, with how well they match and why, so the agent ticks who to email
 * instead of blind-sending to a count (Monday 12567166271).
 *
 * `full` renders the wide table (in the expand modal); the compact variant is
 * the same data as a narrow list for the sidebar.
 */
function BuyerMatchPicker({
  rows, selected, onToggle, onSelectAll, onClearAll,
  selectedCount, selectableTotal, full = false,
}) {
  const selectable = rows.filter((r) => r.sendable);
  const allOn = selectable.length > 0 && selectable.every((r) => selected.has(r.contact_id));

  if (!rows.length) {
    return (
      <p className="text-[11px] text-gray-400 py-3">
        No buyer meets these requirements. Widen them (or tick more suburbs) in "Set requirements &amp;
        add buyers", or search a buyer by name and add them.
      </p>
    );
  }

  const Head = (
    <div className="flex items-center justify-between mb-1.5">
      <label className="flex items-center gap-2 text-xs font-semibold text-gray-600 cursor-pointer">
        <input type="checkbox" checked={allOn} onChange={() => (allOn ? onClearAll() : onSelectAll())} />
        {allOn ? "Untick all" : `Tick all ${selectable.length}`}
      </label>
      <span className="text-[11px] text-gray-400">
        {selectedCount ?? selectable.filter((r) => selected.has(r.contact_id)).length} of{" "}
        {selectableTotal ?? selectable.length} selected
      </span>
    </div>
  );

  if (full) {
    return (
      <>
        {Head}
        <div className="overflow-auto border border-gray-200 rounded-lg">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 text-left text-[11px] uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-3 py-2 w-8"></th>
                <th className="px-3 py-2">Buyer</th>
                <th className="px-3 py-2">Contact</th>
                <th className="px-3 py-2">Match</th>
                <th className="px-3 py-2">Wants</th>
                <th className="px-3 py-2">Budget</th>
                <th className="px-3 py-2">Beds</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Why they match</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((r) => (
                <tr key={r.contact_id || r.email || r.name} className={r.sendable ? "" : "bg-gray-50 text-gray-400"}>
                  <td className="px-3 py-2">
                    <input
                      type="checkbox"
                      disabled={!r.sendable}
                      checked={r.sendable && selected.has(r.contact_id)}
                      onChange={() => onToggle(r.contact_id)}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-semibold text-gray-800 flex items-center gap-1.5">
                      {r.name || "Unnamed buyer"}
                      {r.manual && (
                        <span className="px-1.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold">
                          Added
                        </span>
                      )}
                    </div>
                    {!r.sendable && (
                      <div className="text-[11px] text-amber-600 flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> {r.blocked_reason}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div>{r.email || "-"}</div>
                    <div className="text-gray-400">{r.phone || ""}</div>
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${MATCH_TONE(r.match_score || 0)}`}>
                      {r.match_score ?? "-"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {r.matched_suburb || "-"}
                    {r.suburb_match === "surrounding" && r.matched_distance_km != null && (
                      <span className="text-gray-400"> ({r.matched_distance_km} km)</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">{budgetLabel(r)}</td>
                  <td className="px-3 py-2 text-xs">{bedsLabel(r)}</td>
                  <td className="px-3 py-2 text-xs">{r.pref_property_type || "Any"}</td>
                  <td className="px-3 py-2 text-xs text-gray-500">{(r.reasons || []).join(" · ")}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  }

  return (
    <>
      {Head}
      <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-72 overflow-y-auto">
        {rows.map((r) => (
          <label
            key={r.contact_id || r.email || r.name}
            className={`flex items-start gap-2 px-2 py-1.5 text-xs ${r.sendable ? "cursor-pointer hover:bg-gray-50" : "bg-gray-50"}`}
          >
            <input
              type="checkbox"
              className="mt-0.5"
              disabled={!r.sendable}
              checked={r.sendable && selected.has(r.contact_id)}
              onChange={() => onToggle(r.contact_id)}
            />
            <span className="flex-1 min-w-0">
              <span className="flex items-center gap-1.5">
                <span className={`font-semibold truncate ${r.sendable ? "text-gray-800" : "text-gray-400"}`}>
                  {r.name || "Unnamed buyer"}
                </span>
                {r.manual ? (
                  <span className="px-1.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-bold">
                    Added
                  </span>
                ) : (
                  <span className={`px-1.5 rounded-full text-[10px] font-bold ${MATCH_TONE(r.match_score || 0)}`}>
                    {r.match_score ?? "-"}
                  </span>
                )}
              </span>
              <span className="block text-gray-400 truncate">{r.email || r.phone || "No contact details"}</span>
              <span className="block text-gray-400 truncate">
                {r.matched_suburb} · {budgetLabel(r)}
                {r.pref_property_type ? ` · ${r.pref_property_type}` : ""}
              </span>
              {!r.sendable && (
                <span className="block text-amber-600">{r.blocked_reason}</span>
              )}
            </span>
          </label>
        ))}
      </div>
    </>
  );
}

const ACTIVE_RUN_STATUSES = ["pending_approval", "queued", "sending", "paused"];

export function runHeadline(run, state) {
  if (run.status === "pending_approval") return "This campaign is waiting for approval";
  if (run.status === "paused") return "This campaign's send is paused";
  if (run.status === "sending") return "This campaign is sending right now";
  if (state?.status === "scheduled" && state?.scheduled_at) {
    return `This campaign is scheduled for ${formatMelbourneDateTime(state.scheduled_at)}`;
  }
  return "This campaign is queued to send";
}

export default function CampaignEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = id === "new";

  // Optional property context, passed by the Campaign Manager "Email buyers"
  // hand-off: ?property_id=123&label=14%20Smith%20St
  const propertyId = searchParams.get("property_id");
  const propertyLabel = searchParams.get("label") || "";

  const currentUser = useSelector((state) => state.auth?.user);

  const [campaign, setCampaign] = useState({
    name: "",
    channel: "email",
    subject: "",
    preview_text: "",
    html_body: "",
    // Editable drag-drop builder state; rendered to html_body on every change.
    design_json: {},
    sms_body: "",
    // Sender identity so replies reach a real person, not the default inbox.
    from_name: "",
    from_email: "",
    reply_to: "",
    // Text from COLLINGS even when the agent's own mobile is a sender.
    sms_from_collings: false,
    segment_ids: [],
  });
  // Agent whose details are used as the sender (email) / are written into the
  // message (SMS). Populated from role-management/agents.
  const [agents, setAgents] = useState([]);
  // Whether that list has come back yet - an empty list is a real answer, so
  // anything waiting on it has to wait on the request, not on the length.
  const [agentsLoaded, setAgentsLoaded] = useState(false);
  const [senderAgentId, setSenderAgentId] = useState("");
  const [segments, setSegments] = useState([]);
  // Whether the lists have come back yet. An empty list is a real answer, so the
  // "?prospect_list=" hand-off below has to wait on the REQUEST, not on the
  // length - otherwise a database it cannot find is indistinguishable from one
  // that simply hasn't loaded, and it fails silently (George Bottas, Monday
  // 12975288716).
  const [segmentsLoaded, setSegmentsLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  // The two blocks that ride along with the message, the same pair Find Buyers
  // has (CG, 2026-08-28: "I really like these buttons though so please add it
  // to both email pages"). They live OFF the body while editing and are put
  // back on at save time - see splitEmailBlocks / splitSmsBlocks.
  // Seed for the GeeVee brief box, set by the Campaign Manager hand-off. The
  // box itself lives in AiWritePanel and owns what the agent types.
  const [aiBrief, setAiBrief] = useState("");
  // The agent's choice on the two blocks. On by default on a NEW campaign, the
  // same as Find Buyers, so the two pages behave alike. An existing campaign
  // gets its real state from what is actually on the saved body (see
  // splitEmailBlocks / splitSmsBlocks below), so nothing is silently added to a
  // campaign that never had it.
  //
  // The choice is overridden - both blocks forced on - once a property is in
  // play (CG, Monday 13056948901, 2026-09-16: "Sorry dont make it mandatory by
  // default for emails & texts. Only make it mandatory if an agent populates
  // from a property"). See blocksLocked, below effectivePropertyId.
  //
  // Worth knowing before ticking preferences on a very large list: unlike Find
  // Buyers, which already knows each recipient's link, a campaign resolves
  // {{PreferencesLink}} per contact - up to three queries each
  // (comms_engine._preferences_link_for_contact). It runs in the background
  // send, but on tens of thousands of contacts it is not free.
  const [offMarketChosen, setOffMarketChosen] = useState(isNew);
  const [preferencesChosen, setPreferencesChosen] = useState(isNew);
  // The test box starts on the logged-in agent's own mobile (or email), and can
  // be changed to send to someone else (Simon Abbott, Monday 13392775822).
  // Kept per channel, so an email typed for an email test is not offered as
  // the number for a text; a channel not edited yet follows the agent list.
  const [testToTyped, setTestToTyped] = useState({});
  const [scheduleAt, setScheduleAt] = useState("");
  const [savedId, setSavedId] = useState(isNew ? null : id);
  // Real email attachments (CRM-2026-00032): files sent with every email.
  const [attachments, setAttachments] = useState([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  // Email body view under the composer: a live preview (default) or the raw-HTML
  // fallback. The visual drag-drop builder opens full-screen (builderOpen).
  const [emailMode, setEmailMode] = useState("preview");
  const [builderOpen, setBuilderOpen] = useState(false);
  // Bumped to force the (uncontrolled) builder to remount and re-seed when the
  // body is replaced wholesale (AI write, HTML upload, start-from-template).
  const [builderKey, setBuilderKey] = useState(0);
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [savingTemplate, setSavingTemplate] = useState(false);
  // The {{merge tag}} vocabulary + the compose-scope values currently available
  // (from the selected agent and property). See utils/mergeTags.js.
  const [mergeVocab, setMergeVocab] = useState(null);
  const [resolvedTags, setResolvedTags] = useState({});
  // Why this listing has no buyer-portal page, when it has none. Shown in the
  // composer and used as the {{PropertyLink}} reason in the pre-send guard, so
  // nobody texts a link that opens "Failed to load property details".
  const [linkProblem, setLinkProblem] = useState("");
  // A fresh test send must precede a real send. Reset on any content/audience
  // change so a test of the old version never authorises sending the new one.
  const [hasTested, setHasTested] = useState(false);
  // What the campaign we opened has already done. A campaign is one shot: once
  // it has sent, the backend refuses every edit, every test and a second send,
  // so the editor has to SAY so and offer the way forward instead of showing a
  // live-looking composer whose buttons all fail (Christian Gravias, Monday
  // 13090812859: "Cannot click send on a text on a campaign that I already sent
  // a text from"). The way forward is Duplicate - it keeps the first send's
  // report intact and starts a fresh draft, which is also what stops an SMS
  // being billed and delivered twice on the same record.
  const [sentState, setSentState] = useState(null); // {status, sent_at, sent_count}
  const [duplicating, setDuplicating] = useState(false);
  // A send that is waiting (approval, a scheduled time) or under way on the
  // pre-send engine. What was approved is what goes, so the campaign is
  // read-only until it finishes or is cancelled from its send page.
  const activeRun = ACTIVE_RUN_STATUSES.includes(sentState?.send_run?.status) ? sentState.send_run : null;
  const isLocked = sentState?.status === "sent" || sentState?.status === "sending" || !!activeRun;
  // The shared pre-send preview (who gets it, who is left out, approval).
  const [presend, setPresend] = useState(null);

  // Property picker: pull address/photos/details + listing agent straight from
  // a real CRM listing into the email (esp. the "Just Listed" template).
  // The listings dropped into the email. Multiple are supported: each renders as
  // its own card (Michael Piccoli, Web Dev Team, 2026-09-23). [0] is the primary
  // (drives the SMS + sender agent).
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [propApplying, setPropApplying] = useState(false);
  // Exactly what the picker wrote into the email, so removing the property can
  // take its content back out again instead of leaving a stale preview behind
  // that only a page refresh cleared (Joshua Diokno, 2026-08-05).
  const propertyBodyRef = useRef(null);
  // The SMS message box.
  const smsBodyRef = useRef(null);

  // Inline audience builder ("build a new list" without leaving the editor)
  const [builder, setBuilder] = useState({ contact_type: "", state: "", intent_category: "", has_contactable: true });
  const [liveCount, setLiveCount] = useState(null);
  // Who the chosen lists hold but this send skips, and why (Michael 13 Oct
  // 2026: "79 contacts in it, however, there are only 51 recipients").
  const [leftOut, setLeftOut] = useState(null);
  const [showLeftOut, setShowLeftOut] = useState(false);
  const [counting, setCounting] = useState(false);

  // Buyers Match: the buyers whose saved preferences match the chosen listing,
  // as a table the agent picks from. The TICKED rows are the audience - not the
  // whole match set (Monday 12567166271).
  const [matchRows, setMatchRows] = useState([]);
  const [matchMeta, setMatchMeta] = useState(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [matchError, setMatchError] = useState("");
  const [matchQuery, setMatchQuery] = useState("");
  const [selectedBuyers, setSelectedBuyers] = useState(() => new Set());
  // Buyers the agent explicitly UNTICKED. Kept separately so re-running the
  // search (wider radius, different budget…) doesn't quietly tick them again.
  const [deselectedBuyers, setDeselectedBuyers] = useState(() => new Set());
  const [matchTableOpen, setMatchTableOpen] = useState(false);
  // Buyers Match with NO listing: the agent sets the requirements themselves and
  // matches buyers on saved preferences alone ("add a custom buyer match even if
  // someone doesnt populate a property" - Christian Gravias, 2026-08-05).
  const [customMatch, setCustomMatch] = useState(false);

  // The agent's own search requirements, set up like the "Find Buyers for a
  // Property" panel so the audience isn't stuck with whatever the listing
  // implies (Monday 12567166271). `appliedSearch` is null until they run one -
  // until then the table is the automatic listing match, exactly as before.
  const [criteria, setCriteria] = useState(null);
  const [criteriaDefaults, setCriteriaDefaults] = useState(null);
  const [suburbRows, setSuburbRows] = useState([]);
  const [suburbsLoading, setSuburbsLoading] = useState(false);
  const [selectedSuburbs, setSelectedSuburbs] = useState(() => new Set());
  const [suburbDistances, setSuburbDistances] = useState({});
  const [appliedSearch, setAppliedSearch] = useState(null);
  // Buyers pulled in by name rather than by matching. They always stay in.
  const [addedBuyers, setAddedBuyers] = useState([]);
  const [buyerQuery, setBuyerQuery] = useState("");
  const [buyerResults, setBuyerResults] = useState([]);
  const [buyerSearching, setBuyerSearching] = useState(false);
  // The saved list the ticked buyers are materialised into on send (the send
  // engine only ever resolves lists), reused so repeat sends don't pile up lists.
  const [matchListId, setMatchListId] = useState(null);
  // People added to the send one by one, outside any list (Genneva Smarrelli,
  // CRM-2026-00100: "there needs to be a option to add contacts to send to that
  // are not allocated in specific groups"). Saved as this campaign's own
  // "Added by hand" list, reused on every save so they never pile up.
  const [handPicked, setHandPicked] = useState([]);
  const [handListId, setHandListId] = useState(null);
  const handListLoaded = useRef(false);
  const [pickQuery, setPickQuery] = useState("");
  const [pickResults, setPickResults] = useState([]);
  const [pickSearched, setPickSearched] = useState(false);
  const [newPerson, setNewPerson] = useState(null); // {name, email, phone} while the form is open
  const [addingPerson, setAddingPerson] = useState(false);
  const handPickedKey = handPicked.map((p) => p.id).join(",");

  // Remaining SMS credits on the Mobile Message account (null = unknown/not loaded)
  const [smsBalance, setSmsBalance] = useState(null);
  // Guards the one-shot "?prospect_list=" audience hand-off from the Database.
  const prospectListApplied = useRef(false);
  // Same guard for the "?segment=" hand-off (ticked rows in the Database grid).
  const segmentParamApplied = useRef(false);
  // Same guard for the "?template=just_listed" hand-off from Campaign Manager.
  const justListedApplied = useRef(false);

  const reloadSegments = useCallback(() => {
    fetchSegments()
      .then((rows) => { setSegments(rows); setSegmentsLoaded(true); })
      .catch(() => setSegmentsLoaded(true));
  }, []);

  // Pull the live SMS credit balance once the composer is on the SMS channel.
  useEffect(() => {
    if (campaign.channel !== "sms") return;
    let cancelled = false;
    fetchSmsCreditBalance()
      .then((res) => { if (!cancelled) setSmsBalance(res?.credits ?? null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [campaign.channel]);

  useEffect(() => {
    reloadSegments();
  }, [reloadSegments]);

  // Hand-off from Pipeline > Database: "Email this list" / "Text this list" opens
  // a new campaign with that database already ticked as the audience
  // (?prospect_list=<id>&channel=email|sms — Christian Gravias, 2026-08-03).
  useEffect(() => {
    const wantedList = searchParams.get("prospect_list");
    if (!wantedList || prospectListApplied.current || !segmentsLoaded) return;
    const match = segments.find((s) => String(s.prospect_list_id) === String(wantedList));
    prospectListApplied.current = true;
    if (!match) {
      // The composer only ever sends to a saved list, so with no match there is
      // nothing to tick. Say so instead of opening a blank audience and leaving
      // the agent to work out why nothing was selected.
      toast(
        "We couldn't open that database as an audience. Ask whoever owns it to " +
          "share the list with you, then try again.",
        { icon: "⚠️", duration: 7000 }
      );
      return;
    }
    const wantedChannel = searchParams.get("channel");
    setCampaign((c) => ({
      ...c,
      channel: wantedChannel === "sms" ? "sms" : c.channel,
      name: c.name || match.name.replace(/ \(Database\)$/, ""),
      segment_ids: c.segment_ids.includes(match.id) ? c.segment_ids : [...c.segment_ids, match.id],
    }));
  }, [segments, segmentsLoaded, searchParams]);

  // Hand-off from the Database grid's bulk bar: rows were ticked, the server
  // turned them into a one-off audience, and we open already pointed at it
  // (?segment=<id>&channel=email|sms - Christian Gravias, Monday 12908273441).
  // More than one id can arrive: a selection that covers a whole Database list
  // is sent from that list's own audience rather than from a fresh copy of it,
  // so ticking two whole lists hands over both (Monday 12975288716).
  useEffect(() => {
    const wantedSegment = searchParams.get("segment");
    if (!wantedSegment || segmentParamApplied.current || !segments.length) return;
    const wanted = String(wantedSegment).split(",").map((s) => s.trim()).filter(Boolean);
    const matches = wanted
      .map((id) => segments.find((s) => String(s.id) === id))
      .filter(Boolean);
    if (!matches.length) return;
    segmentParamApplied.current = true;
    const wantedChannel = searchParams.get("channel");
    setCampaign((c) => ({
      ...c,
      channel: wantedChannel === "sms" ? "sms" : c.channel,
      name: c.name || matches[0].name,
      segment_ids: [
        ...c.segment_ids,
        ...matches.map((m) => m.id).filter((id) => !c.segment_ids.includes(id)),
      ],
    }));
  }, [segments, searchParams]);

  // Templates for the "Start from template" picker - scoped to the current
  // channel so an SMS campaign only sees SMS templates and vice versa.
  useEffect(() => {
    fetchTemplates({ channel: campaign.channel })
      .then((r) => setTemplates(r?.results || r || []))
      .catch(() => {});
  }, [campaign.channel]);

  // The {{merge tag}} vocabulary, fetched once. Read from the backend rather
  // than hard-coded here so the chip list, the templates and the send engine can
  // never disagree about which tags exist (Monday 12918907065).
  useEffect(() => {
    fetchMergeTags()
      .then(setMergeVocab)
      .catch(() => {});
  }, []);

  // The builder emits a full HTML document + its editable JSON on every edit.
  // We store both; changing html_body re-arms the mandatory test (via `set`).
  const handleBuilderChange = useCallback(({ html_body, design_json }) => {
    set({ html_body, design_json });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Replace the body from a saved template. For SMS we drop in the message and
  // re-append the chosen agent's contact block; for email we replace the whole
  // design and remount the builder so it re-seeds from the template's HTML.
  const applyTemplate = (tpl) => {
    // Fill the compose-scope tags ({{PropertyAddress}}, {{AgentFirstName}}, ...)
    // right now, from the picked property and agent, so the agent SEES the real
    // wording in the box and can edit it. Per-recipient tags ({{FirstName}}) are
    // deliberately left in place - the send engine fills those per person.
    // Anything still unfilled stays visible as {{Tag}} and is caught by the
    // pre-send guard in doSend (Monday 12918907065).
    const fill = (text) => substituteTags(text, resolvedTags, mergeVocab);

    if (campaign.channel === "sms") {
      const agent = agents.find((a) => String(a.id) === String(senderAgentId));
      set({
        sms_body:
          fill(stripSmsAgentBlock(tpl.sms_body || "")) +
          (agent ? buildSmsAgentBlock(agent) : ""),
      });
    } else {
      set({
        subject: fill(tpl.subject || campaign.subject),
        preview_text: fill(tpl.preview_text || campaign.preview_text),
        html_body: fill(tpl.html_body || ""),
        design_json: tpl.design_json || {},
      });
      setEmailMode("preview");
      setBuilderKey((k) => k + 1);
    }
    setShowTemplates(false);

    const missing = (tpl.required_tags || []).filter((n) => !resolvedTags[n]);
    if (missing.length) {
      toast(
        `Loaded "${tpl.name}" - still needs ${missing.join(", ")}. ` +
          `Pick a property or agent, or edit the message.`,
        { icon: "⚠️", duration: 6000 }
      );
    } else {
      toast.success(`Loaded "${tpl.name}"`);
    }
  };

  // Save the current content as a reusable, shared template on the active channel.
  const saveAsTemplate = async () => {
    const name = window.prompt("Name this template", campaign.name || "New template");
    if (!name) return;
    setSavingTemplate(true);
    try {
      const payload =
        campaign.channel === "sms"
          ? {
              name,
              channel: "sms",
              category: "general",
              // Store the message without the agent-specific block; it's
              // re-appended from the sender picker when the template is used.
              sms_body: stripSmsAgentBlock(campaign.sms_body),
            }
          : {
              name,
              channel: "email",
              category: "general",
              subject: campaign.subject,
              preview_text: campaign.preview_text,
              html_body: campaign.html_body,
              design_json: campaign.design_json,
            };
      const tpl = await createTemplate(payload);
      setTemplates((prev) => [tpl, ...prev]);
      toast.success("Saved as template");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save template");
    } finally {
      setSavingTemplate(false);
    }
  };

  // Load the agent list once for the sender picker (agents + admins).
  useEffect(() => {
    getAgents({ page_size: 100 })
      .then((r) => {
        const list = (r?.data?.results || r?.data || []).filter((a) => a.email);
        setAgents(list);
      })
      .catch(() => {})
      .finally(() => setAgentsLoaded(true));
  }, []);

  // The property in play is either the one picked in-editor or the one handed
  // over from Campaign Manager via the URL. Both drive the audience filter.
  const effectivePropertyId = selectedProperties[0]?.id || propertyId;

  // "Only make it mandatory if an agent populates from a property" (CG, Monday
  // 13056948901, 2026-09-16). Picking a property in the editor, or arriving from
  // the Campaign Manager hand-off with one attached, locks both blocks on;
  // removing the property hands the agent their tick boxes back, on whatever
  // they were last set to.
  const blocksLocked = Boolean(effectivePropertyId);
  const includeOffMarket = blocksLocked || offMarketChosen;
  const includePreferences = blocksLocked || preferencesChosen;

  // The compose-scope tag values available right now, from the picked agent and
  // property. Resolved on the BACKEND (it owns the price label, the auction
  // time in agency-local time and the next open home) and cached here, so
  // applying a template can substitute them instantly with no round trip.
  useEffect(() => {
    if (!senderAgentId && !effectivePropertyId) {
      setResolvedTags({});
      return;
    }
    let cancelled = false;
    checkMergeTags({
      text: "",
      agent_id: senderAgentId || null,
      property_id: effectivePropertyId || null,
    })
      .then((d) => {
        if (cancelled) return;
        setResolvedTags(d?.resolved || {});
        // Why {{PropertyLink}} cannot be filled for THIS listing, when that is
        // the reason (CRM-2026-00081).
        setLinkProblem(d?.property_link_problem || "");
      })
      .catch(() => {
        if (!cancelled) { setResolvedTags({}); setLinkProblem(""); }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [senderAgentId, effectivePropertyId]);
  const effectivePropertyLabel = selectedProperties[0]?.address || propertyLabel;

  // Buyers Match mode: the table's ticked rows are the audience. Reached either by
  // choosing a listing, or by setting the requirements by hand with no listing at
  // all. Otherwise the panel is a plain contact filter (as before).
  const buyerMatchMode = Boolean(effectivePropertyId) || customMatch;

  // A property always wins: choosing one seeds the requirements from the listing,
  // so the hand-set mode steps aside rather than fighting it.
  useEffect(() => {
    if (effectivePropertyId) setCustomMatch(false);
  }, [effectivePropertyId]);

  const builderFilter = useCallback(() => {
    const f = {};
    // In Buyers Match mode the audience is the agent's ticked rows (sent as
    // pinned contacts), NOT a criteria filter - a buyer_match_property_id filter
    // here would union every match back in and undo their picks.
    if (effectivePropertyId || customMatch) return f;
    if (builder.contact_type) f.contact_type = builder.contact_type;
    if (builder.state) f.state = builder.state;
    if (builder.intent_category) f.intent_category = builder.intent_category;
    // "Only contacts reachable by X" refines an actual targeting criterion above -
    // on its own (checked by default, with nothing else set) it isn't real
    // criteria, and made this the ONLY active filter, which resolves to nearly
    // the whole contactable database. That then unions with whatever list/segment
    // is actually selected (e.g. Appraisals/Past Clients), drowning it out - CG,
    // 2026-08-10: still showing 30,065 recipients instead of the scoped list.
    // The channel-based consent filter every send already applies makes this
    // redundant as a standalone criterion anyway.
    if (builder.has_contactable && Object.keys(f).length > 0) {
      f[campaign.channel === "sms" ? "has_phone" : "has_email"] = true;
    }
    return f;
  }, [builder, campaign.channel, effectivePropertyId, customMatch]);

  // Read the agent's unticks / hand-added buyers inside the loader without making
  // it re-fetch every time they tick a box.
  const deselectedRef = useRef(deselectedBuyers);
  const addedRef = useRef(addedBuyers);
  useEffect(() => { deselectedRef.current = deselectedBuyers; }, [deselectedBuyers]);
  useEffect(() => { addedRef.current = addedBuyers; }, [addedBuyers]);
  // Where the listing sits on the map, so surrounding suburbs can be measured.
  // Most CRM listings have no coordinates, so it's usually geocoded from the
  // address here (the backend has no geocoder). `originKeyRef` records which
  // suburb/state the point was looked up for, so changing the suburb moves the
  // centre of the search. It used to be looked up once and kept, which meant a
  // half-typed suburb could stay the origin for the rest of the session.
  const originRef = useRef(null);
  const originKeyRef = useRef("");

  // Requirements belong to ONE listing: pick a different property (or switch to
  // hand-set requirements) and the search starts fresh rather than carrying the
  // last one's filters over.
  useEffect(() => {
    setAppliedSearch(null);
    setAddedBuyers([]);
    setDeselectedBuyers(new Set());
    setBuyerQuery("");
    setBuyerResults([]);
    setCriteria(null);
    setCriteriaDefaults(null);
    setSuburbRows([]);
    setSelectedSuburbs(new Set());
    setSuburbDistances({});
    originRef.current = null;
    originKeyRef.current = "";
  }, [effectivePropertyId, customMatch]);

  // Seed the requirements from the listing (suburb, state, price range, type).
  // With no listing the same endpoint returns blank defaults plus every suburb
  // buyers have marked, so the agent fills the requirements in themselves.
  useEffect(() => {
    if (!effectivePropertyId && !customMatch) return;
    let cancelled = false;
    fetchBuyerMatchCriteria(effectivePropertyId ? { property_id: effectivePropertyId } : {})
      .then((res) => {
        if (cancelled) return;
        const d = res?.defaults || {};
        if (d.lat != null && d.lng != null) originRef.current = { lat: d.lat, lng: d.lng };
        setCriteriaDefaults(d);
        setCriteria(criteriaFromDefaults(d, res?.radius_km || 5));
      })
      .catch(() => { if (!cancelled) setCriteriaDefaults({}); });
    return () => { cancelled = true; };
  }, [effectivePropertyId, customMatch]);

  // The "suburbs buyers have marked" tick-list, refreshed whenever the suburb,
  // state or radius changes. Distances come from Mapbox here, so surrounding
  // suburbs get pre-ticked even though the listing has no coordinates.
  const critSuburb = criteria?.suburb || "";
  const critState = criteria?.state || "";
  const critRadius = criteria?.radius_km || 5;
  useEffect(() => {
    if ((!effectivePropertyId && !customMatch) || !criteriaDefaults) return;
    let cancelled = false;
    setSuburbsLoading(true);
    const t = setTimeout(() => {
      (async () => {
        try {
          // The listing's own coordinates win; otherwise geocode whatever the
          // agent has typed, and do it again whenever they change it. A suburb
          // shorter than three letters is still being typed, so it's ignored
          // rather than pinning the search on a fragment of a word.
          const address = (criteriaDefaults.address || "").trim();
          const originKey = `${address || critSuburb.trim()}|${critState.trim()}`.toLowerCase();
          if (criteriaDefaults.lat != null && criteriaDefaults.lng != null) {
            originRef.current = { lat: criteriaDefaults.lat, lng: criteriaDefaults.lng };
          } else if (originKeyRef.current !== originKey) {
            const usable = Boolean(address) || critSuburb.trim().length >= 3;
            const q = [address || critSuburb.trim(), critState.trim(), "Australia"]
              .filter(Boolean).join(", ");
            originRef.current = usable ? await geocodePlace(q) : null;
            originKeyRef.current = originKey;
          }
          const origin = originRef.current;
          const res = await fetchBuyerMatchCriteria({
            property_id: effectivePropertyId,
            suburb: critSuburb,
            state: critState,
            radius_km: critRadius,
            lat: origin?.lat,
            lng: origin?.lng,
          });
          let rows = res?.suburbs || [];
          let distances = {};
          if (origin) {
            const enriched = await enrichSuburbsWithDistance(rows, origin, critRadius, critState);
            rows = enriched.suburbs;
            distances = enriched.distances;
          }
          if (cancelled) return;
          setSuburbRows(rows);
          setSuburbDistances(distances);
          setSelectedSuburbs(new Set(rows.filter((s) => s.recommended).map((s) => s.suburb)));
        } catch {
          if (!cancelled) setSuburbRows([]);
        } finally {
          if (!cancelled) setSuburbsLoading(false);
        }
      })();
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [effectivePropertyId, customMatch, criteriaDefaults, critSuburb, critState, critRadius]);

  // The search in force. Criteria belong to one listing, so switching property
  // drops back to the automatic match rather than reusing stale filters. With no
  // listing both sides are blank, which is how a hand-set search stays in force.
  const activeSearch =
    appliedSearch &&
    String(appliedSearch.property_id || "") === String(effectivePropertyId || "")
      ? appliedSearch
      : null;
  const activeSearchKey = activeSearch ? JSON.stringify(activeSearch) : "";

  // Load the match table for the chosen listing, pre-ticking everyone reachable
  // on this channel (the old behaviour, blast all matches, stays one click
  // away). Once the agent runs their own requirements, those drive it instead.
  useEffect(() => {
    // A different listing means a different picked-buyers list.
    setMatchListId(null);
    setMatchQuery("");
    // No listing and no hand-set search yet: there is nothing to match on, so the
    // table stays empty until the agent runs their own requirements.
    if (!effectivePropertyId && !activeSearch) {
      setMatchRows([]);
      setMatchMeta(null);
      setMatchError("");
      setSelectedBuyers(new Set());
      return;
    }
    let cancelled = false;
    setMatchLoading(true);
    setMatchError("");
    fetchBuyerMatchTable(effectivePropertyId, campaign.channel, activeSearch)
      .then((res) => {
        if (cancelled) return;
        const rows = res?.rows || [];
        setMatchRows(rows);
        setMatchMeta(res || null);
        setMatchError(res?.detail || "");
        // Everyone reachable starts ticked, except anyone the agent has already
        // unticked, so re-running a wider search doesn't tick them back on.
        const next = new Set();
        rows.forEach((r) => {
          if (r.sendable && !deselectedRef.current.has(r.contact_id)) next.add(r.contact_id);
        });
        addedRef.current.forEach((r) => { if (r.sendable) next.add(r.contact_id); });
        setSelectedBuyers(next);
      })
      .catch(() => {
        if (cancelled) return;
        setMatchRows([]);
        setMatchMeta(null);
        setMatchError("Couldn't load the matching buyers.");
      })
      .finally(() => { if (!cancelled) setMatchLoading(false); });
    return () => { cancelled = true; };
    // activeSearchKey (not activeSearch) so an identical re-render doesn't refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectivePropertyId, campaign.channel, activeSearchKey]);

  // Look up any buyer by name / email / phone so they can be added to the send.
  useEffect(() => {
    const q = buyerQuery.trim();
    if (q.length < 2) { setBuyerResults([]); setBuyerSearching(false); return; }
    setBuyerSearching(true);
    const t = setTimeout(() => {
      searchBuyersToAdd(q, campaign.channel)
        .then((res) => setBuyerResults(res?.rows || []))
        .catch(() => setBuyerResults([]))
        .finally(() => setBuyerSearching(false));
    }, 350);
    return () => clearTimeout(t);
  }, [buyerQuery, campaign.channel]);

  // Switching an email campaign to SMS (or back) changes who can be reached, so
  // re-read the hand-added buyers on the new channel. Without this someone added
  // for their email address stays ticked on a send that has no number for them.
  useEffect(() => {
    const ids = addedRef.current.map((r) => r.contact_id).filter(Boolean);
    if (!ids.length) return;
    let cancelled = false;
    refreshBuyersForChannel(ids, campaign.channel)
      .then((res) => {
        if (cancelled) return;
        const fresh = new Map((res?.rows || []).map((r) => [r.contact_id, r]));
        setAddedBuyers((prev) => prev.map((r) => fresh.get(r.contact_id) || r));
        // Untick anyone this channel can't reach.
        const unreachable = (res?.rows || []).filter((r) => !r.sendable).map((r) => r.contact_id);
        if (unreachable.length) {
          setSelectedBuyers((prevSel) => {
            const next = new Set(prevSel);
            unreachable.forEach((id) => next.delete(id));
            return next;
          });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [campaign.channel]);

  /** Run the agent's requirements instead of the automatic listing match. */
  const runMatchSearch = () => {
    if (!criteria || selectedSuburbs.size === 0) {
      toast.error("Tick at least one suburb first.");
      return;
    }
    setHasTested(false); // audience changed -> re-test required
    setAppliedSearch({
      // Null with no listing: the backend matches on the criteria alone.
      property_id: effectivePropertyId || null,
      suburb: criteria.suburb,
      state: criteria.state,
      lat: originRef.current?.lat ?? criteriaDefaults?.lat ?? null,
      lng: originRef.current?.lng ?? criteriaDefaults?.lng ?? null,
      radius_km: criteria.radius_km,
      suburbs: Array.from(selectedSuburbs),
      suburb_distances: suburbDistances,
      price_min: numOrNull(criteria.price_min),
      price_max: numOrNull(criteria.price_max),
      beds_min: numOrNull(criteria.beds_min),
      beds_max: numOrNull(criteria.beds_max),
      baths_min: numOrNull(criteria.baths_min),
      baths_max: numOrNull(criteria.baths_max),
      cars_min: numOrNull(criteria.cars_min),
      cars_max: numOrNull(criteria.cars_max),
      // The Type dropdown is the whole type criterion; blank means "any", so we
      // don't quietly keep gating on the listing's portal category.
      property_type: criteria.property_type,
      portal_category: "",
    });
  };

  /** Back to the automatic listing match. */
  const resetMatchSearch = () => {
    setAppliedSearch(null);
    setCriteria(criteriaFromDefaults(criteriaDefaults || {}, 5));
    setHasTested(false);
  };

  /** Pull a buyer into the send by hand, match or no match. */
  const addBuyerToSend = (row) => {
    if (!row?.contact_id || !row.sendable) return;
    setHasTested(false);
    setAddedBuyers((prev) =>
      prev.some((r) => r.contact_id === row.contact_id) ? prev : [...prev, row]);
    setDeselectedBuyers((prev) => {
      const next = new Set(prev);
      next.delete(row.contact_id);
      return next;
    });
    setSelectedBuyers((prev) => new Set(prev).add(row.contact_id));
    toast.success(`${row.name || "Buyer"} added to this send`);
  };

  // Matched buyers plus anyone added by name. Added rows sit on top and are
  // flagged `manual`, so a criteria change never drops them.
  const allMatchRows = useMemo(() => {
    const matched = new Set(matchRows.map((r) => r.contact_id).filter(Boolean));
    return [...addedBuyers.filter((r) => !matched.has(r.contact_id)), ...matchRows];
  }, [matchRows, addedBuyers]);
  const addedIds = useMemo(
    () => new Set(addedBuyers.map((r) => r.contact_id)), [addedBuyers]);

  // The refine controls narrow the table (they can't be criteria in this mode).
  // Hand-added buyers are exempt: the agent asked for them by name.
  const refinedMatchRows = allMatchRows.filter((r) => {
    if (r.manual) return true;
    if (builder.has_contactable && !r.sendable) return false;
    if (builder.contact_type && r.contact_type !== builder.contact_type) return false;
    if (builder.state && !stateMatches(r.state, builder.state)) return false;
    if (builder.intent_category && r.intent_category !== builder.intent_category) return false;
    return true;
  });

  // The search box is a lookup, not an audience filter - it hides rows on screen
  // but never silently drops someone from the send.
  const matchQ = matchQuery.trim().toLowerCase();
  const visibleMatchRows = matchQ
    ? refinedMatchRows.filter((r) =>
        `${r.name} ${r.email} ${r.phone} ${r.matched_suburb}`.toLowerCase().includes(matchQ))
    : refinedMatchRows;

  // Who this send actually goes to: ticked AND still inside the refine filters.
  const recipientIds = refinedMatchRows
    .filter((r) => r.sendable && selectedBuyers.has(r.contact_id))
    .map((r) => r.contact_id);
  const recipientKey = recipientIds.join(",");

  const toggleBuyer = (cid) => {
    setHasTested(false); // audience changed -> re-test required
    const wasOn = selectedBuyers.has(cid);
    setSelectedBuyers((prev) => {
      const next = new Set(prev);
      if (wasOn) next.delete(cid);
      else next.add(cid);
      return next;
    });
    // Remember an explicit untick so re-running the search doesn't undo it.
    setDeselectedBuyers((prev) => {
      const next = new Set(prev);
      if (wasOn) next.add(cid);
      else next.delete(cid);
      return next;
    });
  };
  const selectAllVisible = () => {
    setHasTested(false);
    const ids = visibleMatchRows.filter((r) => r.sendable).map((r) => r.contact_id);
    setSelectedBuyers((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
    setDeselectedBuyers((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  };
  const clearAllVisible = () => {
    setHasTested(false);
    const ids = visibleMatchRows.map((r) => r.contact_id);
    setSelectedBuyers((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
    setDeselectedBuyers((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      return next;
    });
  };

  const toggleSuburbTick = (name) => {
    setSelectedSuburbs((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  // Populate the email BODY from a chosen listing: address, photos, features,
  // price, a CTA and the listing agent's signature. We deliberately do NOT set
  // the subject - the agent writes their own. The preview snippet is filled
  // only when still empty (a convenience, never an override).
  // Rebuild the whole email body from the current list of listings. One listing
  // renders exactly as before; two or more stack as cards with a single agent
  // signature at the end. The body is regenerated from the list each time, so
  // adding/removing a listing never leaves stale content behind.
  const renderPropertyList = (list, { firstAdd = false } = {}) => {
    const multi = list.length > 1;
    const intro = multi ? propertyListEmailIntro() : propertyEmailIntro(list[0]);
    const meta = multi ? propertyListEmailMeta(list) : propertyEmailMeta(list[0]);
    const html = buildPropertyEmailHtml(list, { intro });

    // Only fill the preview when it is empty or still the one WE wrote, so an
    // agent's own wording is never overwritten - but it does refresh from single
    // to multi wording as more listings are added.
    const ownedPreview =
      propertyBodyRef.current && campaign.preview_text === propertyBodyRef.current.preview_text;
    const preview =
      !campaign.preview_text || ownedPreview ? meta.preview_text : campaign.preview_text;

    // SMS carries the FIRST listing only - a text can't hold several links
    // without blowing out the credit count (Christian Gravias, 28 Aug 2026).
    // Only ever fills an empty box; a sender's appended contact block is kept.
    const sms = buildPropertyJustListedSms(list[0]);
    const typedSms = stripSmsAgentBlock(campaign.sms_body);
    const agentBlock = campaign.sms_body.slice(typedSms.length);
    const smsBody = typedSms.trim() ? campaign.sms_body : sms + agentBlock;

    if (firstAdd) {
      // Capture the pristine body once, so removing everything can restore it.
      propertyBodyRef.current = {
        prev_html: campaign.html_body,
        prev_design_json: campaign.design_json,
        prev_preview_text: campaign.preview_text,
        prev_sms_body: campaign.sms_body,
      };
    }
    propertyBodyRef.current = {
      ...(propertyBodyRef.current || {}),
      html,
      preview_text: preview,
      sms_body: smsBody,
    };
    set({ preview_text: preview, html_body: html, design_json: {}, sms_body: smsBody });
    setEmailMode("preview");
    setBuilderKey((k) => k + 1);
  };

  // Add a listing to the email. Picking several stacks them as cards.
  const applyProperty = async (item) => {
    if (selectedProperties.some((p) => String(p.id) === String(item.id))) return;
    setPropApplying(true);
    try {
      const data = await fetchPropertyEmailData(item.id);
      const firstAdd = selectedProperties.length === 0;
      const nextList = [...selectedProperties, data];
      setSelectedProperties(nextList);
      renderPropertyList(nextList, { firstAdd });
      // On the first listing, send from its agent if they're in the sender list.
      if (firstAdd && data.agent?.id) {
        const match = agents.find((a) => String(a.id) === String(data.agent.id));
        if (match) applySenderAgent(match.id);
      }
      toast.success(firstAdd ? `Loaded ${data.address}` : `Added ${data.address}`);
    } catch {
      toast.error("Couldn't load that property");
    } finally {
      setPropApplying(false);
    }
  };

  // Remove one listing. Removing the last one restores the pre-property body.
  const removeProperty = (id) => {
    const nextList = selectedProperties.filter((p) => String(p.id) !== String(id));
    if (nextList.length === 0) {
      clearProperty();
      return;
    }
    // If the agent has edited the generated body, confirm before rebuilding it.
    const edited =
      propertyBodyRef.current && (campaign.html_body || "") !== (propertyBodyRef.current.html || "");
    if (edited && !window.confirm(
      "You've edited this email since these listings were added.\n\n" +
        "Removing a listing rebuilds the email from the remaining ones and those edits will be lost. Continue?"
    )) {
      return;
    }
    setSelectedProperties(nextList);
    renderPropertyList(nextList);
  };

  // Removing the property also takes its content back out of the email. Before,
  // the preview kept showing the old listing until the page was refreshed
  // (Joshua Diokno, 2026-08-05). If the agent has since edited that content we
  // ask first rather than silently binning their work.
  const clearProperty = () => {
    const applied = propertyBodyRef.current;
    if (applied) {
      const { untouched, patch } = removePropertyPatch(campaign, applied);
      const ok =
        untouched ||
        window.confirm(
          "You've edited this email since the listing(s) were added.\n\n" +
            "Remove the property content too? Those edits will be lost. Cancel keeps the content and only removes the properties."
        );
      if (ok) {
        // The text goes back too, but only while it is still the one we wrote.
        if (campaign.sms_body === applied.sms_body) {
          patch.sms_body = applied.prev_sms_body || "";
        }
        set(patch);
        setEmailMode("preview");
        setBuilderKey((k) => k + 1); // re-seed the builder from the emptied body
      }
      propertyBodyRef.current = null;
    }
    setSelectedProperties([]);
  };

  // Prefill from the Campaign Manager "Email buyers" / "SMS buyers" hand-off.
  useEffect(() => {
    if (!isNew || !propertyId) return;
    const wantsSms = searchParams.get("channel") === "sms";
    const verb = wantsSms ? "SMS" : "Email";
    setCampaign((c) => ({
      ...c,
      channel: wantsSms ? "sms" : c.channel,
      name: c.name || `${verb} buyers${propertyLabel ? ` · ${propertyLabel}` : ""}`,
    }));
    setAiBrief((b) => b || `Write an email to buyers interested in ${propertyLabel || "this property"}, inviting them to enquire or book an inspection.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, propertyId, propertyLabel]);

  // ...and write the Just Listed message itself, so the composer opens with the
  // property already in it rather than an empty box the agent has to fill from
  // the picker (Christian Gravias, 28 Aug 2026: "If you click Email or SMS
  // button please create an auto template of this property being just listed").
  //
  // Waits for `agents`, because applyProperty also picks the listing agent as
  // the sender and that list arrives separately. Runs once - the ref stops a
  // later agents/channel change re-writing content the agent has since edited.
  useEffect(() => {
    if (!isNew || !propertyId) return;
    if (searchParams.get("template") !== "just_listed") return;
    if (justListedApplied.current || !agentsLoaded) return;
    justListedApplied.current = true;
    applyProperty({ id: propertyId });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNew, propertyId, agentsLoaded, searchParams]);

  // Auto-count recipients whenever the audience changes (debounced). No button.
  // Ticked Buyers Match rows ride along as pinned contacts, so the count is the
  // deduped union of the picked buyers and any saved lists.
  useEffect(() => {
    const f = builderFilter();
    const hasFilter = Object.keys(f).length > 0;
    const pins = Array.from(new Set([
      ...(buyerMatchMode ? recipientKey.split(",").filter(Boolean).map(Number) : []),
      ...handPickedKey.split(",").filter(Boolean).map(Number),
    ]));
    if (!hasFilter && !pins.length && campaign.segment_ids.length === 0) {
      setLiveCount(0);
      setLeftOut(null);
      return;
    }
    setCounting(true);
    const t = setTimeout(() => {
      previewSegment({
        filter_json: hasFilter ? f : {},
        channel: campaign.channel,
        segment_ids: campaign.segment_ids,
        pinned_contact_ids: pins,
      })
        .then((res) => {
          setLiveCount(res.count);
          setLeftOut({
            count: res.left_out_count || 0,
            reasons: res.left_out_reasons || {},
            people: res.left_out || [],
            sharedNumbers: res.shared_number_count || 0,
          });
        })
        .catch(() => { setLiveCount(null); setLeftOut(null); })
        .finally(() => setCounting(false));
    }, 400);
    return () => clearTimeout(t);
  }, [builderFilter, campaign.segment_ids, campaign.channel, buyerMatchMode, recipientKey, handPickedKey]);

  // ---- "Add people": individuals added to this send outside any list ----

  // Re-opening a saved campaign: its "Added by hand" list comes back as the
  // people in the Add people box, not as a ticked list.
  useEffect(() => {
    if (handListLoaded.current || !segmentsLoaded) return;
    const own = segments.find((s) => isHandList(s) && campaign.segment_ids.includes(s.id));
    if (!own) return;
    handListLoaded.current = true;
    // A "Send this again" copy carries the original's list. Its people come
    // across, but the copy saves them into a list of its own, so editing the
    // copy never rewrites who the sent campaign went to.
    if (own.name === `${HAND_LIST_PREFIX}${campaign.name.trim() || "Untitled campaign"}`) {
      setHandListId(own.id);
    }
    setCampaign((c) => ({ ...c, segment_ids: c.segment_ids.filter((x) => x !== own.id) }));
    fetchSegment(own.id)
      .then((seg) => setHandPicked(seg.pinned_members || []))
      .catch(() => toast.error("Couldn't load the people added by hand"));
  }, [segments, segmentsLoaded, campaign.segment_ids, campaign.name]);

  // Contact search for Add people, debounced.
  useEffect(() => {
    const q = pickQuery.trim();
    if (q.length < 2) {
      setPickResults([]);
      setPickSearched(false);
      return;
    }
    const t = setTimeout(() => {
      searchContactsForList(q)
        .then((rows) => setPickResults(rows || []))
        .catch(() => setPickResults([]))
        .finally(() => setPickSearched(true));
    }, 300);
    return () => clearTimeout(t);
  }, [pickQuery]);

  const addHandPicked = (c) => {
    setHasTested(false); // audience changed -> re-test required
    setHandPicked((prev) => (prev.some((p) => p.id === c.id) ? prev : [...prev, c]));
    setPickQuery("");
    setPickResults([]);
  };
  const removeHandPicked = (cid) => {
    setHasTested(false);
    setHandPicked((prev) => prev.filter((p) => p.id !== cid));
  };

  const submitNewPerson = async () => {
    if (!newPerson) return;
    const { name, email, phone } = newPerson;
    if (!email.trim() && !phone.trim()) return toast.error("Enter an email address or a mobile number");
    setAddingPerson(true);
    try {
      const c = await quickAddContact({ name: name.trim(), email: email.trim(), phone: phone.trim() });
      addHandPicked(c);
      setNewPerson(null);
      toast.success(
        c.created
          ? `${c.name} added to your contacts and to this send`
          : `${c.name || "They"} are already in your contacts - added to this send`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't add that person");
    } finally {
      setAddingPerson(false);
    }
  };

  /** Save the Add people box as this campaign's own fixed list and return its
   * id (null when nobody is in it). Same list is reused on every save. */
  const syncHandList = async () => {
    if (!handPicked.length) return null;
    const name = `${HAND_LIST_PREFIX}${campaign.name.trim() || "Untitled campaign"}`;
    const members = { segment_type: "static", filter_json: {}, pinned_contact_ids: handPicked.map((p) => p.id) };
    if (handListId) {
      await updateSegment(handListId, { name, ...members });
      return handListId;
    }
    const seg = await createSegment({ name, ...members });
    setHandListId(seg.id);
    return seg.id;
  };

  /** Name of the saved list the picked buyers live in. Carries the campaign name
   * so two campaigns on the same listing never share (and overwrite) a list. */
  const matchListName = () => {
    // With no listing the suburbs the agent searched are the only meaningful label.
    const suburbs = Array.from(selectedSuburbs).slice(0, 2).join(", ");
    const what = effectivePropertyId
      ? effectivePropertyLabel || `Listing ${effectivePropertyId}`
      : suburbs || "my requirements";
    const base = `Buyers Match · ${what}`;
    const camp = campaign.name.trim();
    return camp ? `${base} · ${camp}` : base;
  };

  /**
   * Materialise the ticked buyers into a saved (static) list and return it.
   * The send engine only ever resolves lists, so this is what makes a hand-picked
   * audience sendable. Re-uses the same list on later edits/sends instead of
   * creating a new one each time - and never renames a list the agent already
   * has (membership is updated, the name is theirs).
   */
  const syncMatchList = async (name = matchListName()) => {
    const members = { segment_type: "static", filter_json: {}, pinned_contact_ids: recipientIds };
    const existingId = matchListId || segments.find((s) => s.name === name)?.id || null;
    if (existingId) {
      await updateSegment(existingId, members);
      setMatchListId(existingId);
      await reloadSegments();
      return { id: existingId, name };
    }
    const seg = await createSegment({ name, ...members });
    setMatchListId(seg.id);
    await reloadSegments();
    return seg;
  };

  const saveAsList = async () => {
    // Buyers Match mode saves the people the agent ticked (a fixed list);
    // contact-filter mode saves the criteria (an auto-updating list).
    const isMatch = buyerMatchMode && recipientIds.length > 0;
    const name = window.prompt("Name this list", isMatch ? matchListName() : "New audience");
    if (!name) return;
    try {
      const seg = isMatch
        ? await syncMatchList(name)
        : await createSegment({ name, segment_type: "dynamic", filter_json: builderFilter() });
      toast.success(isMatch ? `List saved with ${recipientIds.length} buyer${recipientIds.length === 1 ? "" : "s"}` : "List created & selected");
      await reloadSegments();
      if (!campaign.segment_ids.includes(seg.id)) toggleSegment(seg.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed (migrate the DB first)");
    }
  };

  useEffect(() => {
    if (isNew) return;
    fetchCampaign(id)
      .then((c) => {
        // Take the two appended blocks back OFF the saved body so the agent
        // edits their own words only and the tick boxes reflect what is really
        // on the message. persist() puts them back. The older wordings are
        // stripped too (see LEGACY_* in messageBlocks), or the previous sentence
        // stays in the body and the new one is appended beside it.
        const email = splitEmailBlocks(c.html_body || "");
        const sms = splitSmsBlocks(c.sms_body || "");
        const live = c.channel === "sms" ? sms : email;
        setOffMarketChosen(live.offMarket);
        setPreferencesChosen(live.preferences);
        setCampaign({
          name: c.name,
          channel: c.channel,
          subject: c.subject || "",
          preview_text: c.preview_text || "",
          html_body: email.body,
          design_json: c.design_json || {},
          sms_body: sms.body,
          from_name: c.from_name || "",
          from_email: c.from_email || "",
          reply_to: c.reply_to || "",
          sms_from_collings: !!c.sms_from_collings,
          segment_ids: c.segment_ids || [],
        });
        setSavedId(c.id);
        setAttachments(c.attachments || []);
        setSentState({
          status: c.status,
          sent_at: c.sent_at,
          sent_count: c.sent_count || 0,
          total_recipients: c.total_recipients || 0,
          scheduled_at: c.scheduled_at,
          send_run: c.send_run || null,
        });
      })
      .catch(() => toast.error("Couldn't load campaign"));
  }, [id, isNew]);

  const set = (patch) => {
    // Editing content or channel invalidates any prior test.
    if (["subject", "html_body", "sms_body", "channel", "sms_from_collings"].some((k) => k in patch)) {
      setHasTested(false);
    }
    setCampaign((c) => ({ ...c, ...patch }));
  };

  // Visibly auto-populate the Preview text field from the email body (CG,
  // Monday 12975097413: "preview text not auto populating"). Only refreshes a
  // value this effect itself wrote - a hand-typed or saved preview is never
  // overwritten.
  const autoPreviewRef = useRef("");
  useEffect(() => {
    if (campaign.channel !== "email") return;
    const derived = derivePreviewFromBody(campaign.html_body);
    setCampaign((c) => {
      if (c.preview_text && c.preview_text !== autoPreviewRef.current) return c; // hand-typed/saved
      if (c.preview_text === derived) return c;
      autoPreviewRef.current = derived;
      return { ...c, preview_text: derived };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.channel, campaign.html_body]);

  const toggleSegment = (sid) => {
    setHasTested(false); // audience changed -> re-test required
    setCampaign((c) => {
      const has = c.segment_ids.includes(sid);
      return { ...c, segment_ids: has ? c.segment_ids.filter((x) => x !== sid) : [...c.segment_ids, sid] };
    });
  };

  // Pick the agent whose identity the message goes out under. For EMAIL we set
  // the sender name / reply-to so recipient replies land in that agent's inbox
  // (the server keeps the visible From on the authenticated domain). For SMS,
  // which can't be replied to, we write the agent's name + number into the body.
  const applySenderAgent = (agentId) => {
    setSenderAgentId(agentId);
    setHasTested(false); // sender changed -> re-test required
    const agent = agents.find((a) => String(a.id) === String(agentId));
    setCampaign((c) => {
      if (!agent) {
        // "No specific agent" -> clear sender + strip any SMS block.
        return {
          ...c,
          from_name: "",
          from_email: "",
          reply_to: "",
          sms_body: stripSmsAgentBlock(c.sms_body),
        };
      }
      return {
        ...c,
        from_name: agent.name || "",
        from_email: agent.email || "",
        reply_to: agent.email || "",
        sms_body: stripSmsAgentBlock(c.sms_body) + buildSmsAgentBlock(agent),
      };
    });
  };


  // Default the sender to the logged-in user (only for a brand-new email
  // campaign, and only once the agent list has loaded). Never overrides a
  // choice the user has already made or a value loaded from a saved campaign.
  useEffect(() => {
    if (!isNew || senderAgentId || campaign.from_email || !agents.length || !currentUser?.email) return;
    const me = agents.find((a) => a.email?.toLowerCase() === currentUser.email.toLowerCase());
    if (me) applySenderAgent(me.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agents, currentUser, isNew]);

  // `overrides` lets a caller save fields it has just computed (e.g. the audience
  // list the picked buyers were materialised into) without waiting for a re-render.
  // An agent is free to paste either merge tag into the body themselves; if they
  // have, the block is not added a second time and the card below says so.
  // That holds whether the block is ticked or locked on by a property.
  const offMarketInBody = (campaign.channel === "sms" ? campaign.sms_body : campaign.html_body || "").includes(OFF_MARKET_TAG);
  const preferencesInBody = (campaign.channel === "sms" ? campaign.sms_body : campaign.html_body || "").includes(PREFERENCES_TAG);
  const addsOffMarket = includeOffMarket && !offMarketInBody;
  const addsPreferences = includePreferences && !preferencesInBody;

  /** The body fields as they should be SAVED - blocks re-attached. */
  const withBlocks = (draft) => {
    const opts = {
      offMarket: includeOffMarket && !(draft.channel === "sms" ? draft.sms_body : draft.html_body || "").includes(OFF_MARKET_TAG),
      preferences: includePreferences && !(draft.channel === "sms" ? draft.sms_body : draft.html_body || "").includes(PREFERENCES_TAG),
    };
    if (draft.channel === "sms") {
      return { sms_body: assembleSms(draft.sms_body, opts) };
    }
    // Email keeps its automatic unsubscribe button from the backend
    // (comms_engine._send_email), so only the two optional blocks go in here.
    return { html_body: appendEmailBlocks(draft.html_body, { ...opts, unsubscribe: false }) };
  };

  // The text exactly as it will go out: the agent's words, then each block that
  // is on, on its own line. Every credit count on this page prices THIS, not the
  // typed body - a block on the message is a block the carrier bills for.
  // From the agent's own mobile it also opens with their intro line
  // (Monday 13392775822). Blank sender = the campaign's creator, as the send does.
  const agentSmsSender = useSmsSender(senderAgentId, campaign.channel === "sms");
  const smsSender = campaign.sms_from_collings ? COLLINGS_SENDER : agentSmsSender;
  const smsOutgoing = withSmsIntro(assembleSms(campaign.sms_body, {
    offMarket: addsOffMarket, preferences: addsPreferences,
  }), smsSender.intro);
  const smsOutgoingInfo = smsInfo(asSent(pricedText(smsOutgoing)));
  // Over the provider's limit the text is refused outright, test or real
  // (CRM-2026-00125), so say so before anyone presses a button.
  const smsOutgoingLength = smsLength(asSent(pricedText(smsOutgoing)));
  const smsTooLong = campaign.channel === "sms" && smsOutgoingLength > SMS_MAX_CHARS;

  // What the preferences line adds to an SMS bill. Priced the way the send is
  // priced: merge tags expanded, links shortened, opt-out clause added. Shown
  // whether the agent ticked it or a property locked it on - either way they are
  // about to pay for it.
  const smsWithoutPreferences = withSmsIntro(
    assembleSms(campaign.sms_body, { offMarket: addsOffMarket }), smsSender.intro);
  const preferencesExtraCredits = campaign.channel !== "sms" ? 0 : Math.max(
    0,
    smsInfo(asSent(pricedText(`${smsWithoutPreferences}

${PREFERENCES_SENTENCE_SMS}`))).credits
      - smsInfo(asSent(pricedText(smsWithoutPreferences))).credits,
  );

  // Send this one again: copy it back to a fresh draft and open that. The
  // original keeps its own report (who it went to, opens, clicks), and the copy
  // starts at zero, so "send it again" never overwrites what the first send did
  // or re-bills the same SMS on the same record.
  const doDuplicate = async () => {
    if (!savedId) return;
    setDuplicating(true);
    try {
      const copy = await duplicateCampaign(savedId);
      toast.success("Copied to a new draft - edit it and send");
      navigate(`/communications/campaigns/${copy.id}`);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't copy this campaign"));
    } finally {
      setDuplicating(false);
    }
  };

  const persist = async (overrides = null) => {
    if (isLocked) {
      // The backend would refuse this anyway ("Cannot edit a campaign that has
      // sent"); say what to do instead of relaying a dead end.
      toast.error(activeRun
        ? "This campaign is waiting to send. Cancel the send to edit it."
        : "This campaign has already sent. Use 'Send this again' to make an editable copy.");
      return null;
    }
    if (!campaign.name.trim()) {
      toast.error("Give the campaign a name");
      return null;
    }
    const draft = overrides ? { ...campaign, ...overrides } : campaign;
    if (overrides) setCampaign((c) => ({ ...c, ...overrides }));
    // People in the Add people box ride along as this campaign's own list. It
    // is added to what is saved only, never to the ticked lists on screen, so
    // removing someone from the box takes them straight off the count.
    let handId = null;
    try {
      handId = await syncHandList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the people you added");
      return null;
    }
    const segmentIds = handId && !draft.segment_ids.includes(handId)
      ? [...draft.segment_ids, handId]
      : draft.segment_ids;
    // The Off Market Portal invitation and the preferences line are put back on
    // here, at the last moment, rather than living in the body the agent edits.
    // That is what makes the tick boxes honest: tick one and it is in the saved
    // message, untick it and it is gone, and re-opening the campaign splits them
    // off again so nobody ends up typing around them or saving a second copy
    // (CG, 2026-08-28).
    const payload = { ...draft, ...withBlocks(draft) };
    payload.segment_ids = segmentIds;
    setSaving(true);
    try {
      let saved;
      if (savedId) {
        saved = await updateCampaign(savedId, payload);
      } else {
        saved = await createCampaign(payload);
        setSavedId(saved.id);
        window.history.replaceState(null, "", `/communications/campaigns/${saved.id}`);
      }
      toast.success("Saved");
      return saved;
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
      return null;
    } finally {
      setSaving(false);
    }
  };

  // Attach a real file to the email (CRM-2026-00032). The campaign needs an id
  // to hang the file on, so save the draft first if it's brand new.
  const handleAttachmentUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // allow re-picking the same file after a remove
    if (!file) return;
    if ((file.size || 0) > 10 * 1024 * 1024) {
      toast.error("That file is larger than the 10 MB limit.");
      return;
    }
    let campaignId = savedId;
    if (!campaignId) {
      const saved = await persist();
      if (!saved) return; // persist already toasted why (e.g. missing name)
      campaignId = saved.id;
    }
    setUploadingAttachment(true);
    try {
      const att = await uploadCampaignAttachment(campaignId, file);
      setAttachments((prev) => [...prev, att]);
      toast.success(`Attached ${att.filename}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not attach that file.");
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleAttachmentRemove = async (attachmentId) => {
    if (!savedId) return;
    const prev = attachments;
    setAttachments((list) => list.filter((a) => a.id !== attachmentId)); // optimistic
    try {
      await deleteCampaignAttachment(savedId, attachmentId);
    } catch (err) {
      setAttachments(prev); // put it back on failure
      toast.error(err?.response?.data?.detail || "Could not remove that file.");
    }
  };

  const handleHtmlUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      // Wipe any prior builder design so the builder re-seeds from this HTML.
      set({ html_body: String(reader.result || ""), design_json: {} });
      setEmailMode("preview");
      setBuilderKey((k) => k + 1);
      toast.success(`Loaded ${file.name}`);
    };
    reader.onerror = () => toast.error("Couldn't read that file");
    reader.readAsText(file);
    e.target.value = "";
  };

  // --- Files dropped on the GeeVee writer -------------------------------
  // Images and PDFs only. Each file is sent up as soon as it lands so the agent
  // sees straight away whether we could read it, rather than finding out when
  // they press Generate.
  // What GeeVee's draft does to the campaign. The panel itself (the brief box,
  // the image/PDF drop zone, Generate) is components/AiWritePanel, shared with
  // the Find Buyers composer.
  const applyAiResult = (res) => {
    const agent = agents.find((a) => String(a.id) === String(senderAgentId));
    if (campaign.channel === "sms") {
      // Re-append the chosen agent's contact block after the fresh copy.
      set({ sms_body: stripSmsAgentBlock(res.body) + (agent ? buildSmsAgentBlock(agent) : "") });
    } else {
      // Fresh AI copy replaces the body; drop the old design and remount the
      // builder so the new HTML seeds the canvas.
      set({ subject: res.subject, preview_text: res.preview_text, html_body: res.body_html, design_json: {} });
      setEmailMode("preview");
      setBuilderKey((k) => k + 1);
    }
  };

  const aiAudienceHint = segments
    .filter((sg) => campaign.segment_ids.includes(sg.id))
    .map((sg) => sg.name)
    .join(", ");

  const myTestAddress = (() => {
    if (campaign.channel === "email") return currentUser?.email || "";
    const myEmail = (currentUser?.email || "").toLowerCase();
    const me = myEmail && agents.find((a) => (a.email || "").toLowerCase() === myEmail);
    return me?.phone_number || currentUser?.phone_number || "";
  })();
  const testTo = testToTyped[campaign.channel] ?? myTestAddress;

  const doTest = async () => {
    const saved = await persist();
    if (!saved) return;
    try {
      const res = await testSendCampaign(saved.id, testTo.trim());
      // The backend answers a refused test with an error now, but never call a
      // failed send "sent" again (CRM-2026-00125).
      if (res.status === "failed") {
        toast.error(res.detail || res.error || "The test could not be sent");
        return;
      }
      setHasTested(true);
      toast.success(res.simulated ? `Simulated test to ${res.sent_to} (sending is off)` : `Test sent to ${res.sent_to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    }
  };

  const doSend = async (schedule) => {
    // A test send is no longer required before a real send (DOMA 13090812859).
    // Agents can still self-test with the Test button, but it no longer gates
    // Send. The merge-tag gap check and recipient confirm below remain as the
    // pre-send guardrails.
    // The buyers ticked in the Buyers Match table become a saved list on the
    // campaign, so the send reaches exactly the people on screen.
    let segmentIds = campaign.segment_ids;
    if (buyerMatchMode && recipientIds.length) {
      try {
        const seg = await syncMatchList();
        segmentIds = Array.from(new Set([...segmentIds, seg.id]));
      } catch (e) {
        return toast.error(e?.response?.data?.detail || "Couldn't save the selected buyers");
      }
    }
    if (!segmentIds.length && !handPicked.length) {
      return toast.error(
        buyerMatchMode ? "Tick the buyers you want to email" : "Pick at least one audience list, or add people"
      );
    }
    // Pre-send merge-tag guard. The send engine replaces a tag it can't fill
    // with an EMPTY STRING, so "{{PropertyAddress}} goes to auction
    // {{AuctionDate}}" would go out to the whole audience as "goes to auction ."
    // - invisible until it has already landed. Ask first (Monday 12918907065).
    const draftText = isEmail
      ? `${campaign.subject || ""}\n${campaign.html_body || ""}`
      : campaign.sms_body || "";
    const gaps = unresolvedTags(mergeVocab, draftText, resolvedTags,
      linkProblem ? { PropertyLink: linkProblem } : {});
    if (gaps.length) {
      const ok = window.confirm(
        `This message has merge tags with nothing to fill them in.\n\n` +
          gaps.map((g) => `  {{${g.name}}} - ${g.reason}`).join("\n") +
          `\n\n${unresolvedSummary(gaps)}\n\nSend anyway?`
      );
      if (!ok) return;
    }

    if (schedule && !scheduleAt) return toast.error("Pick a date and time first");
    const saved = await persist({ segment_ids: segmentIds });
    if (!saved) return;
    // Every campaign send goes through the shared pre-send check: who gets it,
    // who is left out and why, approval when it is large, then a gradual send
    // (CG, Monday 13391839919). The dialog creates the send and opens its page.
    const params = { campaign_id: saved.id };
    if (schedule) params.send_after = datetimeLocalToUtcISOString(scheduleAt);
    setPresend({ kind: `campaign_${campaign.channel}`, params, schedule });
  };

  const isEmail = campaign.channel === "email";

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <button onClick={() => navigate("/communications")} className="inline-flex items-center gap-1 text-sm text-gray-500 mb-3 hover:text-gray-700">
        <ArrowLeft className="w-4 h-4" /> Back to Communications
      </button>

      {/* A campaign that has already gone out. Everything below still reads
          normally so the agent can see exactly what was sent, but it cannot be
          edited or sent a second time - the one button that works is the copy. */}
      {activeRun && (
        <div className="mb-4 rounded-xl border border-sky-200 bg-sky-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <p className="text-sm font-bold text-sky-900 inline-flex items-center gap-2">
              <Lock className="w-4 h-4" />
              {runHeadline(activeRun, sentState)}
            </p>
            <p className="text-xs text-sky-800 mt-1">
              It can't be edited while it is waiting to send, so what goes out is what was checked. Open the
              send to follow it, or cancel it there to make changes.
            </p>
          </div>
          <button
            onClick={() => navigate(`/send-runs/${activeRun.id}`)}
            className="px-3 py-2 rounded-lg bg-[#55C5D0] text-white text-sm font-bold inline-flex items-center justify-center gap-2 shrink-0"
          >
            <Send className="w-4 h-4" /> View send
          </button>
        </div>
      )}

      {isLocked && !activeRun && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <p className="text-sm font-bold text-amber-900 inline-flex items-center gap-2">
              <Lock className="w-4 h-4" />
              {sentState.status === "sending"
                ? "This campaign is sending right now"
                : "This campaign has already been sent"}
              {sentState.sent_at && sentState.status === "sent" && (
                <span className="font-normal">
                  {" "}on {formatMelbourneDateTime(sentState.sent_at)}
                </span>
              )}
            </p>
            <p className="text-xs text-amber-800 mt-1">
              It is kept exactly as it went out so its report stays accurate
              {sentState.sent_count ? ` (${sentState.sent_count.toLocaleString()} sent)` : ""}.
              To {isEmail ? "email" : "text"} again, make a copy - it opens as a fresh draft with
              the same message and audience, ready to edit and send.
            </p>
          </div>
          <button
            onClick={doDuplicate}
            disabled={duplicating}
            className="px-3 py-2 rounded-lg bg-[#55C5D0] text-white text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50 shrink-0"
          >
            <Copy className="w-4 h-4" />
            {duplicating ? "Copying…" : "Send this again"}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Composer */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <input
              value={campaign.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder="Campaign name (internal)"
              className="w-full text-lg font-bold border-0 border-b border-gray-100 focus:ring-0 focus:border-[#55C5D0] pb-2 mb-3"
            />
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1 w-fit">
              {["email", "sms"].map((ch) => (
                <button
                  key={ch}
                  onClick={() => set({ channel: ch })}
                  className={`flex items-center gap-2 px-4 py-1.5 rounded-lg text-sm font-bold ${
                    campaign.channel === ch ? "bg-white text-[#55C5D0] shadow-sm" : "text-gray-500"
                  }`}
                >
                  {ch === "sms" ? <MessageSquare className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                  {ch.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          {/* AI writer - shared with the Find Buyers composer so both pages
              get the same "Write with GeeVee" box (CG, 2026-08-28). */}
          <AiWritePanel
            channel={campaign.channel}
            onWritten={applyAiResult}
            audienceHint={aiAudienceHint}
            initialBrief={aiBrief}
            /* Once a listing is picked, GeeVee gets its portal /p/<slug> link as
               the CTA instead of inventing a website page that 404s. */
            propertyId={effectivePropertyId}
            /* Footer "Need a hand? Email ..." should read as the sending agent,
               so the preview matches the real send. */
            supportEmail={campaign.from_email || currentUser?.email || ""}
          />

          {/* Content */}
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            {isEmail ? (
              <>
                {/* Property picker: fill the email from a real listing.
                    The panel itself is shared with the Find Buyers composer, so
                    the two pages have the same section rather than two that
                    drift (CG, 2026-09-01: "Please make it the same"). */}
                <PropertyPickerPanel
                  multiple
                  selectedItems={selectedProperties}
                  onPick={applyProperty}
                  onRemove={removeProperty}
                  busy={propApplying}
                />

                {/* This listing has no page on the buyer portal, so a property
                    link in the message would open an error for the recipient.
                    Said here, while it can still be fixed, rather than after a
                    few hundred people have tapped it (CRM-2026-00081). */}
                {linkProblem ? (
                  <p className="text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2">
                    {linkProblem}
                  </p>
                ) : null}

                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Subject</label>
                  <input value={campaign.subject} onChange={(e) => set({ subject: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">Preview text</label>
                  <input value={campaign.preview_text} onChange={(e) => set({ preview_text: e.target.value })} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
                </div>

                {/* Attachments (CRM-2026-00032): real files sent with the email.
                    Positioned directly under Preview text per Christian's request. */}
                <div className="rounded-lg border border-gray-200 p-3">
                  <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                    <div className="flex items-center gap-2">
                      <Paperclip className="w-4 h-4 text-[#25AFC0]" />
                      <p className="font-bold text-gray-800 text-sm">Attachments</p>
                    </div>
                    <label className={`cursor-pointer inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold border border-gray-200 ${uploadingAttachment ? "opacity-50 pointer-events-none" : "text-gray-600 hover:text-gray-800"}`}>
                      {uploadingAttachment ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />}
                      {uploadingAttachment ? "Uploading…" : "Attach file"}
                      <input type="file" className="hidden" onChange={handleAttachmentUpload} disabled={uploadingAttachment} />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mb-2">
                    PDFs, images or documents (up to 10 MB each) sent as real attachments with every email.
                  </p>
                  {attachments.length === 0 ? (
                    <p className="text-xs text-gray-400">No files attached.</p>
                  ) : (
                    <ul className="space-y-1">
                      {attachments.map((a) => (
                        <li key={a.id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-2.5 py-1.5">
                          <FileText className="w-4 h-4 text-gray-400 shrink-0" />
                          <span className="text-sm text-gray-700 truncate flex-1">{a.filename}</span>
                          {a.size ? (
                            <span className="text-[11px] text-gray-400 shrink-0">
                              {a.size < 1024 * 1024
                                ? `${Math.max(1, Math.round(a.size / 1024))} KB`
                                : `${(a.size / (1024 * 1024)).toFixed(1)} MB`}
                            </span>
                          ) : null}
                          <button
                            type="button"
                            onClick={() => handleAttachmentRemove(a.id)}
                            className="text-gray-400 hover:text-red-600 shrink-0"
                            title="Remove attachment"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                    <label className="block text-xs font-medium text-gray-500">
                      Body - drag blocks to design; use {"{{FirstName}}"}, {"{{Suburb}}"} merge tags
                    </label>
                    <div className="flex items-center gap-2 flex-wrap">
                    {/* Start from a saved template (centered popover, never overflows) */}
                    <button
                      type="button"
                      onClick={() => setShowTemplates(true)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold text-gray-600 hover:text-gray-800 border border-gray-200"
                    >
                      <LayoutTemplate className="w-3.5 h-3.5" /> Templates
                    </button>
                    {/* Save current design as a reusable template */}
                    <button
                      type="button"
                      onClick={saveAsTemplate}
                      disabled={savingTemplate || !campaign.html_body}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold text-gray-600 hover:text-gray-800 border border-gray-200 disabled:opacity-50"
                    >
                      <Save className="w-3.5 h-3.5" /> {savingTemplate ? "Saving…" : "Save as template"}
                    </button>
                    <label className="cursor-pointer inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold text-gray-600 hover:text-gray-800 border border-gray-200">
                      <Upload className="w-3.5 h-3.5" /> Upload HTML
                      <input type="file" accept=".html,.htm,text/html" className="hidden" onChange={handleHtmlUpload} />
                    </label>
                    <div className="flex gap-1 bg-gray-100 rounded-lg p-0.5">
                      {[
                        { key: "preview", label: "Preview", Icon: Eye },
                        { key: "html", label: "HTML", Icon: Code },
                      ].map(({ key, label, Icon }) => (
                        <button
                          key={key}
                          type="button"
                          onClick={() => setEmailMode(key)}
                          className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold ${emailMode === key ? "bg-white text-[#55C5D0] shadow-sm" : "text-gray-500"}`}
                        >
                          <Icon className="w-3.5 h-3.5" /> {label}
                        </button>
                      ))}
                    </div>
                    </div>
                  </div>

                  {/* Primary action: open the full-screen drag-and-drop designer. */}
                  <button
                    type="button"
                    onClick={() => setBuilderOpen(true)}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-[#55C5D0] text-white text-sm font-bold hover:bg-[#3fb4bf] mb-3"
                  >
                    <Blocks className="w-4 h-4" /> {campaign.html_body ? "Edit design (full screen)" : "Design email (full screen)"}
                  </button>

                  {emailMode === "preview" ? (
                    campaign.html_body ? (
                      <iframe
                        title="Email preview"
                        srcDoc={campaign.html_body}
                        className="w-full rounded-lg border border-gray-200 bg-white"
                        style={{ height: 620 }}
                      />
                    ) : (
                      <div className="w-full rounded-lg border border-dashed border-gray-300 p-10 text-center text-sm text-gray-400" style={{ minHeight: 200 }}>
                        Click <strong>Design email</strong> to build it visually, write with GeeVee, or start from a template.
                      </div>
                    )
                  ) : (
                    <>
                      {/* The designer re-opens from design_json when there is one, so an
                          HTML edit drops it; otherwise re-opening the designer brought
                          back the old design over this edit (CRM-2026-00129). */}
                      <textarea value={campaign.html_body} onChange={(e) => set({ html_body: e.target.value, design_json: {} })} rows={20} data-gramm="false" data-gramm_editor="false" data-enable-grammarly="false" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm font-mono" />
                      <p className="mt-1 text-[11px] text-gray-400">
                        Edits here are kept when you re-open the designer, which rebuilds its blocks from this HTML.
                      </p>
                    </>
                  )}


                  {/* What will be added, shown where the body is - the lines
                      were being honoured but were only visible in a preview
                      elsewhere on the page (CG, 2026-09-01: "I have ticked both
                      buttons but its not showing up in the email"). */}
                  <MessageBlocksTail
                    channel="email"
                    offMarket={includeOffMarket && !offMarketInBody}
                    preferences={includePreferences && !preferencesInBody}
                  />

                  {/* The two blocks Christian liked on Find Buyers, now on this
                      page too (CG, 2026-08-28: "I really like these buttons
                      though so please add it to both email pages"). They are
                      attached to the body when the campaign is saved. */}
                  <MessageExtras
                    className="mt-3"
                    channel="email"
                    offMarket={includeOffMarket}
                    onOffMarketChange={setOffMarketChosen}
                    preferences={includePreferences}
                    onPreferencesChange={setPreferencesChosen}
                    offMarketInBody={offMarketInBody}
                    preferencesInBody={preferencesInBody}
                    locked={blocksLocked}
                  />

                  {builderOpen && (
                    <EmailBuilder
                      key={builderKey}
                      title={campaign.name ? `Design email - ${campaign.name}` : "Design email"}
                      design={campaign.design_json}
                      html={campaign.html_body}
                      preheader={campaign.preview_text}
                      onChange={handleBuilderChange}
                      onClose={() => setBuilderOpen(false)}
                    />
                  )}

                  <TemplatePickerModal
                    open={showTemplates}
                    onClose={() => setShowTemplates(false)}
                    templates={templates}
                    onPick={applyTemplate}
                    channel="email"
                    resolvedTags={resolvedTags}
                  />
                </div>
              </>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-1 flex-wrap gap-2">
                  <label className="block text-xs font-medium text-gray-500">
                    Message - use {"{{FirstName}}"}, {"{{Suburb}}"} ({smsOutgoing.length} chars ·{" "}
                    <span className={smsOutgoingInfo.credits > 1 ? "text-amber-600 font-semibold" : ""}>
                      {smsOutgoingInfo.credits} credit{smsOutgoingInfo.credits === 1 ? "" : "s"} each
                    </span>
                    {smsOutgoingInfo.encoding === "Unicode" ? " · Unicode" : ""})
                  </label>
                  <div className="flex items-center gap-2 flex-wrap">
                    {/* Puts the unsubscribe link at the end of the text, whatever the
                        cursor is on (Simon Abbott, Monday 13392775822: "when the agent
                        clicks "insert unsubscribe link" it automatically puts it at the
                        end of the text message"). The send moves it below the portal and
                        preferences lines too, so it is always the very last line. */}
                    <button
                      type="button"
                      onClick={() => {
                        if (campaign.sms_body.includes(SMS_STOP_PLACEHOLDER)) {
                          toast.error("This message already has an unsubscribe link placeholder.");
                          return;
                        }
                        set({ sms_body: appendStopPlaceholder(campaign.sms_body) });
                      }}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold text-gray-600 hover:text-gray-800 border border-gray-200"
                    >
                      <Link2 className="w-3.5 h-3.5" /> Insert unsubscribe link
                    </button>
                    {/* Start from a saved SMS template */}
                    <button
                      type="button"
                      onClick={() => setShowTemplates(true)}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold text-gray-600 hover:text-gray-800 border border-gray-200"
                    >
                      <LayoutTemplate className="w-3.5 h-3.5" /> Templates
                    </button>
                    {/* Save current message as a reusable SMS template */}
                    <button
                      type="button"
                      onClick={saveAsTemplate}
                      disabled={savingTemplate || !campaign.sms_body.trim()}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-bold text-gray-600 hover:text-gray-800 border border-gray-200 disabled:opacity-50"
                    >
                      <Save className="w-3.5 h-3.5" /> {savingTemplate ? "Saving…" : "Save as template"}
                    </button>
                  </div>
                </div>
                <SmsIntroHead intro={smsSender.intro} sender={smsSender.sender} />
                <textarea ref={smsBodyRef} value={campaign.sms_body} onChange={(e) => set({ sms_body: e.target.value })} rows={5} data-gramm="false" data-gramm_editor="false" data-enable-grammarly="false" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />

                {/* The ticked lines, attached to the box that holds the message
                    (CG, 2026-09-01: "I cant see it added in the text
                    message"). */}
                <MessageBlocksTail
                  channel="sms"
                  offMarket={includeOffMarket && !offMarketInBody}
                  preferences={includePreferences && !preferencesInBody}
                  unsubscribePlaced={campaign.sms_body.includes(SMS_STOP_PLACEHOLDER)}
                />

                {smsTooLong && (
                  <p role="alert" className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs font-medium text-red-700">
                    Too long to send: this text is {smsOutgoingLength.toLocaleString()} characters once the
                    links and unsubscribe line are added. The most one SMS can be is{" "}
                    {SMS_MAX_CHARS.toLocaleString()} characters (10 credits), and anything longer is refused
                    and never arrives. Please shorten the message.
                  </p>
                )}

                {/* Credit breakdown - each SMS "part" (160 chars, or 70 if Unicode) costs 1 credit */}
                {campaign.sms_body.trim() && (
                  <p className="mt-1 text-xs text-gray-500">
                    This message sends as{" "}
                    <span className={smsOutgoingInfo.credits > 1 ? "font-semibold text-amber-600" : "font-semibold text-gray-700"}>
                      {smsOutgoingInfo.credits} credit{smsOutgoingInfo.credits === 1 ? "" : "s"}
                    </span>{" "}
                    per recipient
                    {smsOutgoingInfo.credits > 1
                      ? ` (over ${smsOutgoingInfo.encoding === "Unicode" ? 70 : 160} characters, so it splits into ${smsOutgoingInfo.credits} texts)`
                      : "."}
                    {smsOutgoingInfo.encoding === "Unicode" && " Emoji/special characters make this a Unicode message (70 chars per credit)."}
                    {" "}
                    Ends with an "Unsubscribe" link, always the last line.
                    {/collings\.com\.au|zaltech\.studio/i.test(smsOutgoing)
                      && " Links to our own site are tracked, so the sign-ups and enquiries they"
                         + " produce show up under Analytics > Campaign Leads, and shortened to"
                         + " collings.com.au/l/... so the tracking costs a few characters instead"
                         + " of about fifty."}
                  </p>
                )}


                {/* Same two blocks on the text side (CG, 2026-08-28: "we like
                    the Invite to the off market portal & let them update their
                    preferences buttons that we want to add to both pages
                    also"). Each lands on its own line, blank line between,
                    like the Unsubscribe clause. */}
                <MessageExtras
                  className="mt-3"
                  channel="sms"
                  offMarket={includeOffMarket}
                  onOffMarketChange={setOffMarketChosen}
                  preferences={includePreferences}
                  onPreferencesChange={setPreferencesChosen}
                  offMarketInBody={offMarketInBody}
                  preferencesInBody={preferencesInBody}
                  preferencesExtraCredits={preferencesExtraCredits}
                  recipientCount={liveCount || 0}
                  locked={blocksLocked}
                />

                <TemplatePickerModal
                  open={showTemplates}
                  onClose={() => setShowTemplates(false)}
                  templates={templates}
                  onPick={applyTemplate}
                  channel="sms"
                  resolvedTags={resolvedTags}
                />
              </div>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Sender / agent identity */}
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-1">
              <UserCircle className="w-4 h-4 text-gray-400" />
              <p className="font-bold text-gray-800 text-sm">{isEmail ? "From agent" : "Agent to contact"}</p>
            </div>
            <p className="text-xs text-gray-500 mb-3">
              {isEmail
                ? "Choose the agent this email is from. Replies go straight to them."
                : "Recipients can't reply to a broadcast SMS, so the agent you pick is written into the message with their number to call."}
            </p>
            <select
              value={senderAgentId}
              onChange={(e) => applySenderAgent(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm"
            >
              <option value="">{isEmail ? "Default Collings sender" : "No agent (none added)"}</option>
              {agents.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name || a.email}{a.title ? ` · ${a.title}` : ""}
                </option>
              ))}
            </select>

            {!isEmail && (
              <SmsSendFrom
                className="mt-3"
                agentSender={agentSmsSender}
                fromCollings={!!campaign.sms_from_collings}
                onChange={(v) => set({ sms_from_collings: v })}
              />
            )}

            {senderAgentId && (() => {
              const a = agents.find((x) => String(x.id) === String(senderAgentId));
              if (!a) return null;
              return (
                <div className="mt-3 rounded-lg bg-[#f6fdfe] border border-[#d6f0f3] p-3 text-xs text-gray-600 space-y-1">
                  <div className="flex items-center gap-2 font-semibold text-gray-800">
                    <UserCircle className="w-3.5 h-3.5 text-[#25AFC0]" /> {a.name || a.email}
                  </div>
                  {isEmail ? (
                    <>
                      <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-gray-400" /> Sent from {a.email}</div>
                      <p className="text-[11px] text-gray-400">
                        The email is sent from this agent's Collings address (From, replies and the footer contact), delivered over the authenticated Collings domain.
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-2">
                        <Phone className="w-3.5 h-3.5 text-gray-400" />
                        {a.phone_number ? a.phone_number : <span className="text-amber-600">No phone number on file</span>}
                      </div>
                      <p className="text-[11px] text-gray-400">Added to the end of the SMS so recipients can call back.</p>
                      <p className="text-[11px] text-gray-400">
                        {smsSender.own_number
                          ? `Sends from this agent's own mobile (${smsSender.sender}), so it starts with "${smsSender.intro}"`
                          : 'Sends from the "COLLINGS" sender.'}
                      </p>
                    </>
                  )}
                </div>
              );
            })()}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <Users className="w-4 h-4 text-gray-400" />
              <p className="font-bold text-gray-800 text-sm">Audience</p>
            </div>

            {/* Saved lists */}
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Your lists</p>
            <div className="space-y-2 max-h-40 overflow-y-auto">
              {segments
                .filter((s) => s.source !== "database" && !isHandList(s))
                .map((s) => (
                  <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox" checked={campaign.segment_ids.includes(s.id)} onChange={() => toggleSegment(s.id)} />
                    <span className="flex-1">{s.name}</span>
                    {/* A restricted list is not shown to anyone outside its
                        tier at all (the backend filters it out), so this badge
                        is just so the people who CAN see it know which lists
                        the rest of the office cannot. */}
                    {s.visibility === "admin" && (
                      <span
                        className="px-1.5 py-0.5 rounded bg-purple-50 text-[10px] font-bold text-purple-600"
                        title="Only admins can see or send to this list"
                      >
                        ADMIN
                      </span>
                    )}
                    {s.visibility === "super_admin" && (
                      <span
                        className="px-1.5 py-0.5 rounded bg-rose-50 text-[10px] font-bold text-rose-600"
                        title="Only the super admin can see or send to this list"
                      >
                        SUPER ADMIN
                      </span>
                    )}
                    <span className="text-xs text-gray-400" title="Total contacts in this list, before filtering by phone/email or consent">
                      {s.live_count ?? s.cached_count ?? "-"} total
                    </span>
                  </label>
                ))}
              {segments.filter((s) => s.source !== "database" && !isHandList(s)).length === 0 && (
                <p className="text-xs text-gray-400">No saved lists yet - build one below.</p>
              )}
            </div>

            {/* Databases built under Pipeline > Database are audiences too, so an
                agent can text or email a list they just uploaded
                (Christian Gravias, 2026-08-03). A database a colleague SHARED
                with me is in here as well, tagged with whose it is - George
                Bottas, Monday 12975288716. */}
            {segments.some((s) => s.source === "database") && (
              <>
                <p className="mt-3 text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1 flex items-center gap-1.5">
                  <Database className="w-3.5 h-3.5" /> From the Database
                </p>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {segments
                    .filter((s) => s.source === "database")
                    .map((s) => {
                      // Shared with me by a colleague rather than one of mine.
                      const sharedBy =
                        currentUser?.id && s.owner && String(s.owner) !== String(currentUser.id)
                          ? s.owner_name || "another agent"
                          : "";
                      return (
                        <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            checked={campaign.segment_ids.includes(s.id)}
                            onChange={() => toggleSegment(s.id)}
                          />
                          <span className="flex-1">
                            {s.name.replace(/ \(Database\)$/, "")}
                            {sharedBy && (
                              <span
                                className="ml-1.5 px-1.5 py-0.5 rounded bg-sky-50 text-[10px] font-bold text-sky-600"
                                title={`Shared with you by ${sharedBy}`}
                              >
                                {sharedBy}
                              </span>
                            )}
                          </span>
                          <span className="text-xs text-gray-400" title="Total contacts in this list, before filtering by phone/email or consent">
                            {s.live_count ?? s.cached_count ?? "-"} total
                          </span>
                        </label>
                      );
                    })}
                </div>
              </>
            )}

            {/* Add people: anyone, one by one, without them being in a list
                (Genneva Smarrelli, CRM-2026-00100). Sent alongside any ticked
                lists, deduped, consent still applied. */}
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="flex items-center justify-between mb-1">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide flex items-center gap-1.5">
                  <UserPlus className="w-3.5 h-3.5" /> Add people
                </p>
                {!isLocked && !newPerson && (
                  <button
                    onClick={() => setNewPerson({ name: "", email: "", phone: "" })}
                    className="text-[11px] font-bold text-[#25AFC0] hover:underline"
                  >
                    + New person
                  </button>
                )}
              </div>
              <p className="text-[11px] text-gray-400 mb-2">
                Send to someone who isn't in any list. Search your contacts, or add a new person.
              </p>
              {!isLocked && (
                <div className="relative mb-2">
                  <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-2" />
                  <input
                    value={pickQuery}
                    onChange={(e) => setPickQuery(e.target.value)}
                    placeholder="Search by name, email or mobile"
                    className="w-full border border-gray-300 rounded-lg pl-7 pr-2 py-1 text-xs"
                  />
                  {pickQuery.trim().length >= 2 && (pickResults.length > 0 || pickSearched) && (
                    <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                      {pickResults.map((r) => {
                        const already = handPicked.some((p) => p.id === r.id);
                        return (
                          <button
                            key={r.id}
                            onClick={() => addHandPicked(r)}
                            disabled={already}
                            className="w-full text-left px-2.5 py-1.5 hover:bg-gray-50 flex justify-between items-center gap-2 disabled:opacity-50"
                          >
                            <span className="text-xs text-gray-800 truncate">{r.name || "(no name)"}</span>
                            <span className="text-[11px] text-gray-400 truncate">
                              {already ? "Added" : (isEmail ? r.email || r.phone : r.phone || r.email)}
                            </span>
                          </button>
                        );
                      })}
                      {pickSearched && pickResults.length === 0 && (
                        <p className="px-2.5 py-1.5 text-[11px] text-gray-400">No contact matches that.</p>
                      )}
                      <button
                        onClick={() => {
                          setNewPerson(guessNewPerson(pickQuery));
                          setPickQuery("");
                          setPickResults([]);
                        }}
                        className="w-full text-left px-2.5 py-1.5 border-t border-gray-100 text-[11px] font-bold text-[#25AFC0] hover:bg-[#eefcfd]"
                      >
                        + Add "{pickQuery.trim()}" as a new person
                      </button>
                    </div>
                  )}
                </div>
              )}

              {newPerson && (
                <div className="mb-2 rounded-lg border border-[#55C5D0] bg-[#eefcfd] p-2 space-y-1.5">
                  <input
                    value={newPerson.name}
                    onChange={(e) => setNewPerson((p) => ({ ...p, name: e.target.value }))}
                    placeholder="Full name"
                    className="w-full border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white"
                  />
                  <input
                    value={newPerson.email}
                    onChange={(e) => setNewPerson((p) => ({ ...p, email: e.target.value }))}
                    placeholder="Email"
                    type="email"
                    className="w-full border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white"
                  />
                  <input
                    value={newPerson.phone}
                    onChange={(e) => setNewPerson((p) => ({ ...p, phone: e.target.value }))}
                    placeholder="Mobile"
                    className="w-full border border-gray-300 rounded-lg px-2 py-1 text-xs bg-white"
                  />
                  <p className="text-[10px] text-gray-500">
                    They are saved to your contacts. If the email or mobile is already a contact, that contact is used.
                  </p>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setNewPerson(null)} className="text-[11px] text-gray-500 px-2 py-1">
                      Cancel
                    </button>
                    <button
                      onClick={submitNewPerson}
                      disabled={addingPerson}
                      className="text-[11px] font-bold px-2.5 py-1 rounded-md bg-[#55C5D0] text-white disabled:opacity-50"
                    >
                      {addingPerson ? "Adding…" : "Add to this send"}
                    </button>
                  </div>
                </div>
              )}

              {handPicked.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {handPicked.map((p) => {
                    // Flag anyone this channel can't reach, so a chip on screen
                    // never reads as a guaranteed send.
                    const unreachable = isEmail ? !p.email : !p.phone;
                    return (
                      <span
                        key={p.id}
                        title={unreachable
                          ? `No ${isEmail ? "email" : "mobile"} on file - will be skipped`
                          : (isEmail ? p.email : p.phone) || ""}
                        className={`inline-flex items-center gap-1 text-xs pl-2 pr-1 py-0.5 rounded-full ${
                          unreachable ? "bg-gray-100 text-gray-400 line-through" : "bg-[#E9F8F9] text-[#2A8791]"
                        }`}
                      >
                        {p.name || p.email || p.phone}
                        {!isLocked && (
                          <button onClick={() => removeHandPicked(p.id)} className="hover:text-red-500" aria-label="Remove">
                            <X className="w-3 h-3" />
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Inline builder — Buyers Match */}
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Buyers Match</p>
                {buyerMatchMode && (
                  <button
                    onClick={() => setMatchTableOpen(true)}
                    className="text-[11px] font-bold text-[#25AFC0] inline-flex items-center gap-1 hover:underline"
                  >
                    <Maximize2 className="w-3 h-3" /> Full table
                  </button>
                )}
              </div>
              {buyerMatchMode ? (
                <>
                  <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[#eefcfd] text-[#25AFC0] text-xs font-semibold">
                      <Users className="w-3 h-3" />
                      {effectivePropertyId
                        ? `Buyers matching ${effectivePropertyLabel || "this property"}`
                        : "Buyers matching your own requirements"}
                    </span>
                    {activeSearch && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-50 text-amber-700 text-[11px] font-semibold">
                        <SlidersHorizontal className="w-3 h-3" /> Your requirements
                      </span>
                    )}
                    {addedBuyers.length > 0 && (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-violet-50 text-violet-700 text-[11px] font-semibold">
                        +{addedBuyers.length} added by hand
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-gray-400 mb-2">
                    Buyers whose saved preferences (suburb, budget &amp; type) match{" "}
                    {effectivePropertyId ? "this listing" : "the requirements you set"}. Tick who to
                    email - only the ticked buyers are sent to.
                  </p>
                  <button
                    onClick={() => setMatchTableOpen(true)}
                    className="w-full mb-2 px-2 py-1.5 rounded-lg border border-[#55C5D0] text-[#25AFC0] text-xs font-bold inline-flex items-center justify-center gap-1.5 hover:bg-[#eefcfd]"
                  >
                    <SlidersHorizontal className="w-3.5 h-3.5" />
                    Set requirements &amp; add buyers
                  </button>
                  {!effectivePropertyId && (
                    <button
                      onClick={() => setCustomMatch(false)}
                      className="w-full mb-2 text-[11px] font-semibold text-gray-400 hover:text-gray-600"
                    >
                      Cancel, filter my contacts instead
                    </button>
                  )}
                </>
              ) : (
                <>
                  <p className="text-[11px] text-gray-400 mb-2">
                    Pick a property above to match buyers by their saved preferences, or set the
                    requirements yourself - no listing needed.
                  </p>
                  {/* No listing to hand? Match buyers on requirements you type in
                      (Christian Gravias, 2026-08-05). */}
                  <button
                    onClick={() => {
                      setCustomMatch(true);
                      setMatchTableOpen(true);
                    }}
                    className="w-full mb-2 px-2 py-1.5 rounded-lg border border-[#55C5D0] text-[#25AFC0] text-xs font-bold inline-flex items-center justify-center gap-1.5 hover:bg-[#eefcfd]"
                  >
                    <SlidersHorizontal className="w-3.5 h-3.5" />
                    Match buyers without a property
                  </button>
                </>
              )}

              {/* The pick-and-choose table */}
              {buyerMatchMode && (
                <div className="mb-3">
                  {matchLoading ? (
                    <p className="text-[11px] text-gray-400 py-2">Finding matching buyers…</p>
                  ) : matchError ? (
                    <p className="text-[11px] text-amber-600 py-2 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" /> {matchError}
                    </p>
                  ) : !effectivePropertyId && !activeSearch ? (
                    <p className="text-[11px] text-gray-400 py-2">
                      Set your requirements above (suburb, budget, beds, type) and run the search to
                      see the buyers who match.
                    </p>
                  ) : (
                    <>
                      {allMatchRows.length > 6 && (
                        <div className="relative mb-1.5">
                          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-2" />
                          <input
                            value={matchQuery}
                            onChange={(e) => setMatchQuery(e.target.value)}
                            placeholder="Search these buyers"
                            className="w-full border border-gray-300 rounded-lg pl-7 pr-2 py-1 text-xs"
                          />
                        </div>
                      )}
                      <BuyerMatchPicker
                        rows={visibleMatchRows}
                        selected={selectedBuyers}
                        onToggle={toggleBuyer}
                        onSelectAll={selectAllVisible}
                        onClearAll={clearAllVisible}
                        selectedCount={recipientIds.length}
                        selectableTotal={refinedMatchRows.filter((r) => r.sendable).length}
                      />
                      {matchMeta?.blocked_count > 0 && (
                        <p className="mt-1 text-[11px] text-gray-400">
                          {matchMeta.blocked_count} match{matchMeta.blocked_count === 1 ? "" : "es"} can't be
                          reached by {campaign.channel === "sms" ? "SMS" : "email"} (unsubscribed or no
                          {campaign.channel === "sms" ? " number" : " address"}).
                        </p>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="space-y-2">
                {buyerMatchMode && (
                  <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Narrow the list</p>
                )}
                <select value={builder.contact_type} onChange={(e) => { setBuilder((b) => ({ ...b, contact_type: e.target.value })); setLiveCount(null); }} className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
                  <option value="">Any contact type</option>
                  {CONTACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
                <div className="grid grid-cols-2 gap-2">
                  <StateAutocomplete
                    value={builder.state}
                    onChange={(v) => { setBuilder((b) => ({ ...b, state: v })); setLiveCount(null); }}
                  />
                  <select value={builder.intent_category} onChange={(e) => { setBuilder((b) => ({ ...b, intent_category: e.target.value })); setLiveCount(null); }} className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-sm">
                    <option value="">Any intent</option>
                    {INTENT.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input type="checkbox" checked={builder.has_contactable} onChange={(e) => { setBuilder((b) => ({ ...b, has_contactable: e.target.checked })); setLiveCount(null); }} />
                  {buyerMatchMode ? "Only show buyers" : "Only contacts"} reachable by {campaign.channel === "sms" ? "SMS" : "email"}
                </label>
              </div>

              <div className="flex justify-end mt-3">
                <button onClick={saveAsList} className="text-xs font-bold px-2 py-1 rounded-md bg-gray-100 text-gray-700">
                  {buyerMatchMode && recipientIds.length
                    ? `Save ${recipientIds.length} selected as list`
                    : "Save as list"}
                </button>
              </div>
            </div>

            {/* Live recipient count - updates automatically as you change the audience */}
            <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between">
              <span className="text-sm text-gray-500">Will send to</span>
              <span className="text-base font-bold text-gray-900">
                {counting ? (
                  <span className="text-gray-400">counting…</span>
                ) : liveCount !== null ? (
                  `${liveCount.toLocaleString()} recipient${liveCount === 1 ? "" : "s"}`
                ) : (
                  "-"
                )}
              </span>
            </div>
            {/* Ticking suburbs alone doesn't load an audience - the search must
                be run. An agent staring at "0 recipients" with suburbs ticked
                needs to be told the missing step, not left to guess (live
                report, 18 Sep 2026: suburbs ticked, blast stuck on 0). */}
            {!counting && buyerMatchMode && recipientIds.length === 0 &&
              selectedSuburbs.size > 0 && !activeSearch && allMatchRows.length === 0 && (
              <p className="mt-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                You've ticked suburbs but haven't run the search yet — press{" "}
                <span className="font-bold">"Find buyers"</span> in step 3 to load your audience.
              </p>
            )}
            {!counting && liveCount !== null && leftOut && leftOut.count > 0 ? (
              <div className="mt-1 text-[11px] text-gray-500">
                <p>
                  <span className="font-semibold text-gray-700">{leftOut.count.toLocaleString()} on your list{campaign.segment_ids.length === 1 ? "" : "s"} won't get this</span>
                  {" - "}
                  {Object.entries(leftOut.reasons).map(([reason, n]) => `${n} ${reason.toLowerCase()}`).join(", ")}.{" "}
                  <button type="button" onClick={() => setShowLeftOut((v) => !v)} className="font-semibold text-teal-600 hover:underline">
                    {showLeftOut ? "Hide" : "See who"}
                  </button>
                </p>
                {showLeftOut && (
                  <ul className="mt-1.5 max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                    {leftOut.people.map((p) => (
                      <li key={p.id} className="flex items-center justify-between gap-2 px-2.5 py-1">
                        <span className="text-gray-700 truncate">{p.name}</span>
                        <span className="text-gray-400 shrink-0">{p.reason}</span>
                      </li>
                    ))}
                    {leftOut.count > leftOut.people.length && (
                      <li className="px-2.5 py-1 text-gray-400">and {(leftOut.count - leftOut.people.length).toLocaleString()} more</li>
                    )}
                  </ul>
                )}
                {campaign.channel === "sms" && leftOut.reasons["No mobile number"] > 0 && (
                  <p className="mt-1 text-gray-400">Add a mobile to their Database row and they'll be included next time.</p>
                )}
              </div>
            ) : !counting && liveCount !== null && (campaign.segment_ids.length > 0 || buyerMatchMode || handPicked.length > 0) && (
              <p className="mt-1 text-[11px] text-gray-400">
                Lower than a list's "total"? Some contacts are excluded here - no {campaign.channel === "sms" ? "phone number" : "email"} on file, opted out, or marked do-not-contact.
              </p>
            )}
            {!counting && liveCount !== null && campaign.channel === "sms" && leftOut && leftOut.sharedNumbers > 0 && (
              <p className="mt-1 text-[11px] text-gray-400">
                {leftOut.sharedNumbers} mobile number{leftOut.sharedNumbers === 1 ? " is" : "s are"} on more than one contact (duplicate rows) - the same text only goes to a number once.
              </p>
            )}

            {/* Total SMS credits = credits per message x recipients. SMS only. */}
            {campaign.channel === "sms" && campaign.sms_body.trim() && liveCount !== null && !counting && (
              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm text-gray-500">Estimated credits</span>
                <span className="text-base font-bold text-teal-600">
                  {(smsOutgoingInfo.credits * liveCount).toLocaleString()} credit
                  {smsOutgoingInfo.credits * liveCount === 1 ? "" : "s"}
                </span>
              </div>
            )}
            {campaign.channel === "sms" && campaign.sms_body.trim() && liveCount !== null && !counting && (
              <p className="mt-1 text-[11px] text-gray-400 text-right">
                {smsOutgoingInfo.credits} credit{smsOutgoingInfo.credits === 1 ? "" : "s"} × {liveCount.toLocaleString()} recipient{liveCount === 1 ? "" : "s"} (estimate; personalised fields may change length)
              </p>
            )}

            {/* Actual credits remaining on the Mobile Message account */}
            {campaign.channel === "sms" && smsBalance !== null && (
              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm text-gray-500">Credits remaining</span>
                <span className="text-base font-bold text-gray-900">{smsBalance.toLocaleString()}</span>
              </div>
            )}
            {/* Warn when this send would use more than the account has left */}
            {campaign.channel === "sms" && campaign.sms_body.trim() && smsBalance !== null && liveCount !== null && !counting &&
              smsOutgoingInfo.credits * liveCount > smsBalance && (
                <p className="mt-1 text-xs font-semibold text-red-600">
                  Not enough credits — this send needs {(smsOutgoingInfo.credits * liveCount).toLocaleString()} but only {smsBalance.toLocaleString()} remain. Top up before sending.
                </p>
              )}
          </div>

          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            {/* Sent: the save / test / send trio all 400 on the backend, so show
                the one action that works instead of three that don't. */}
            {activeRun ? (
              <button
                onClick={() => navigate(`/send-runs/${activeRun.id}`)}
                className="w-full px-3 py-2 rounded-lg bg-[#55C5D0] text-white text-sm font-bold inline-flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" /> View send
              </button>
            ) : isLocked ? (
              <>
                <button
                  onClick={doDuplicate}
                  disabled={duplicating}
                  className="w-full px-3 py-2 rounded-lg bg-[#55C5D0] text-white text-sm font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Copy className="w-4 h-4" />
                  {duplicating ? "Copying…" : "Send this again"}
                </button>
                <p className="text-xs text-gray-500">
                  Opens a copy as a new draft. This one stays as sent, with its report.
                </p>
              </>
            ) : (
              <>
              <button onClick={() => persist()} disabled={saving} className="w-full px-3 py-2 rounded-lg border border-gray-300 text-sm font-bold text-gray-700 disabled:opacity-50">
                {saving ? "Saving…" : "Save draft"}
              </button>

              {/* Optional test - self-test stays available but no longer gates
                  the real send (DOMA 13090812859). */}
              <div className="flex gap-2">
                <input value={testTo} onChange={(e) => { const value = e.target.value; setTestToTyped((t) => ({ ...t, [campaign.channel]: value })); }} placeholder={isEmail ? "you@email.com" : "04xxxxxxxx"} className="flex-1 border border-gray-300 rounded-lg px-2 py-2 text-sm" />
                <button
                  onClick={doTest}
                  title="Send test"
                  className={`px-3 py-2 rounded-lg text-sm font-bold inline-flex items-center gap-1 ${
                    hasTested ? "bg-green-100 text-green-700" : "bg-gray-800 text-white"
                  }`}
                >
                  {hasTested ? <Check className="w-4 h-4" /> : <Beaker className="w-4 h-4" />}
                  {hasTested ? "Tested" : "Test"}
                </button>
              </div>

              {!hasTested && (
                <p className="text-xs text-gray-500 -mt-1">
                  Optional: send a test to yourself first to preview it.
                </p>
              )}

              {/* Send opens the pre-send check; nothing goes until it is confirmed there. */}
              <button
                onClick={() => doSend(false)}
                title="Check who gets it, then send"
                className="w-full px-3 py-2 rounded-lg bg-[#55C5D0] text-white text-sm font-bold inline-flex items-center justify-center gap-2"
              >
                <Send className="w-4 h-4" /> Send...
              </button>

              <div className="flex gap-2">
                <input type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} className="flex-1 border border-gray-300 rounded-lg px-2 py-2 text-sm" />
                <button onClick={() => doSend(true)} disabled={!scheduleAt} title={scheduleAt ? "Schedule" : "Pick a date and time first"} className="px-3 py-2 rounded-lg bg-amber-100 text-amber-700 text-sm font-bold inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed">
                  <Clock className="w-4 h-4" />
                </button>
              </div>
              </>
            )}
          </div>
        </div>
      </div>

      {presend && (
        <PreSendDialog
          kind={presend.kind}
          params={presend.params}
          title={presend.schedule ? "Check and schedule this campaign" : "Check and send this campaign"}
          noun={isEmail ? "email" : "text"}
          onClose={() => setPresend(null)}
        />
      )}

      {/* The Buyers Match table, full width: every match with the score, what they
          want and why they matched, so the agent can choose properly. */}
      {matchTableOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setMatchTableOpen(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-6xl my-8 flex flex-col max-h-[88vh]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-100 p-4">
              <div>
                <h3 className="font-bold text-gray-900">
                  {effectivePropertyId
                    ? `Buyers matching ${effectivePropertyLabel || "this listing"}`
                    : "Buyers matching your requirements"}
                </h3>
                <p className="text-xs text-gray-500">
                  {!effectivePropertyId && "No listing needed - t"}
                  {effectivePropertyId && "T"}ick at least one suburb, set the requirements that
                  count, then tick who to
                  {campaign.channel === "sms" ? " text" : " email"}. Buyers who aren't a match can be
                  searched for and added by hand.
                </p>
              </div>
              <button onClick={() => setMatchTableOpen(false)} className="text-gray-400 hover:text-gray-700">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 flex-1 overflow-auto space-y-4">
              {criteria ? (
                <div className="border border-gray-200 rounded-xl p-4">
                  <BuyerMatchSearchPanel
                    propertyLabel={effectivePropertyId ? effectivePropertyLabel : ""}
                    criteria={criteria}
                    onCriteria={(key, value) => setCriteria((c) => ({ ...c, [key]: value }))}
                    suburbs={suburbRows}
                    suburbsLoading={suburbsLoading}
                    selectedSuburbs={selectedSuburbs}
                    onToggleSuburb={toggleSuburbTick}
                    onSetSuburbs={(names) => setSelectedSuburbs(new Set(names))}
                    onRun={runMatchSearch}
                    running={matchLoading}
                    onReset={resetMatchSearch}
                    isCustom={Boolean(activeSearch)}
                    channel={campaign.channel}
                    searchQuery={buyerQuery}
                    onSearchQuery={setBuyerQuery}
                    searchResults={buyerResults}
                    searching={buyerSearching}
                    onAddBuyer={addBuyerToSend}
                    addedIds={addedIds}
                  />
                </div>
              ) : (
                <p className="text-xs text-gray-400">Loading the search requirements…</p>
              )}

              <div>
                <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide mb-2">
                  4 · Who this goes to
                </p>
                <div className="relative mb-3 max-w-xs">
                  <Search className="w-4 h-4 text-gray-400 absolute left-2 top-2.5" />
                  <input
                    value={matchQuery}
                    onChange={(e) => setMatchQuery(e.target.value)}
                    placeholder="Find someone in this list"
                    className="w-full border border-gray-300 rounded-lg pl-8 pr-2 py-1.5 text-sm"
                  />
                </div>
                <BuyerMatchPicker
                  full
                  rows={visibleMatchRows}
                  selected={selectedBuyers}
                  onToggle={toggleBuyer}
                  onSelectAll={selectAllVisible}
                  onClearAll={clearAllVisible}
                  selectedCount={recipientIds.length}
                  selectableTotal={refinedMatchRows.filter((r) => r.sendable).length}
                />
              </div>
            </div>
            <div className="border-t border-gray-100 p-4 flex items-center justify-between">
              <span className="text-sm text-gray-500">
                {recipientIds.length} buyer{recipientIds.length === 1 ? "" : "s"} selected
              </span>
              <button
                onClick={() => setMatchTableOpen(false)}
                className="px-3 py-2 rounded-lg bg-[#55C5D0] text-white text-sm font-bold inline-flex items-center gap-1"
              >
                <Check className="w-4 h-4" /> Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
